/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import AnnoSelector from "@/components/AnnoSelector";

const TIPI_LABEL = {
  testo: "Testo", testo_lungo: "Testo lungo (note)", numero: "Numero", percentuale: "Percentuale (%)",
  data: "Data", lista: "Lista personalizzabile", multi_lista: "Lista a scelta multipla",
  spunta: "Spunta (sì/no)", anagrafica: "Anagrafica alunni (sola lettura)", file: "File / Documento", link: "Link / URL",
  email: "Email", pagato: "Pagato (automatico)",
};

const fmtDT = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const cellText = (col, v) => {
  if (v == null || v === "") return "";
  if (col.tipo === "spunta") return v ? "sì" : "";
  if (col.tipo === "file") return v && v.filename ? v.filename : "";
  if (col.tipo === "multi_lista") return Array.isArray(v) ? v.join(", ") : String(v);
  if (col.tipo === "percentuale") return v === "" || v == null ? "" : `${v}%`;
  if (col.tipo === "pagato") return v && v.importo != null ? `${fmtEuro(v.importo)}${v.data ? " · " + v.data : ""}` : "";
  return String(v);
};

const fmtEuro = (n) => "€ " + Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function PagatoBadge({ v }) {
  if (!v || v.importo == null) return <span style={{ color: "#cfd8dc" }}>—</span>;
  return (
    <span data-testid="pagato-badge" style={{ display: "inline-block", background: "#2e7d32", color: "white", fontWeight: 700, fontSize: 11, padding: "3px 8px", borderRadius: 4, whiteSpace: "nowrap" }}
      title={`Iscrizione pagata${v.data ? " il " + v.data : ""}`}>
      {fmtEuro(v.importo)}{v.data ? ` · ${v.data}` : ""}
    </span>
  );
}

function ConfermatoBadge() {
  return (
    <span data-testid="conferma-badge" style={{ display: "inline-block", background: "#1d4ed8", color: "white", fontWeight: 700, fontSize: 11, padding: "3px 10px", borderRadius: 999, whiteSpace: "nowrap", letterSpacing: 0.3 }}
      title="Iscrizione confermata">
      Confermato
    </span>
  );
}

export default function TabelleConfigPage() {
  const [tab, setTab] = useState("compila");
  return (
    <div data-testid="tabelle-config-page" style={{ padding: "12px 20px" }}>
      <div className="sw-page-title">
        <h1>🧩 Tabelle personalizzate</h1>
        <div className="sw-breadcrumb">Home &gt; Tabelle Alunni &gt; Tabelle personalizzate</div>
      </div>
      <div style={{ margin: "0 0 12px", display: "flex", gap: 2, borderBottom: "2px solid #1565c0" }}>
        <TabBtn active={tab === "compila"} onClick={() => setTab("compila")} testid="tc-tab-compila">📋 Modelli attivi</TabBtn>
        <TabBtn active={tab === "modelli"} onClick={() => setTab("modelli")} testid="tc-tab-modelli">🛠 Modelli & colonne</TabBtn>
      </div>
      {tab === "compila" ? <CompilaGrid /> : <ModelliBuilder />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      style={{ padding: "8px 18px", border: "none", background: active ? "#1565c0" : "white", color: active ? "white" : "#1565c0",
        fontWeight: 700, fontSize: 12, cursor: "pointer", borderRadius: "4px 4px 0 0", borderTop: "1px solid #ccc", borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc" }}>
      {children}
    </button>
  );
}

/* ==================== BUILDER MODELLI ==================== */
function ModelliBuilder() {
  const { annoCorrente, user } = useAuth();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ tipi: [], anagrafica_campi: [] });
  const [editing, setEditing] = useState(null); // null | model object
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("");

  const catList = useMemo(() => Array.from(new Set(items.map(m => m.categoria).filter(Boolean))).sort(), [items]);
  const shownItems = useMemo(() => items.filter(m => !filterCat || (filterCat === "__none__" ? !m.categoria : m.categoria === filterCat)), [items, filterCat]);

  const isAdmin = user?.role === "admin" || !!(user?.gestioni || {}).admin;
  const canEdit = (m) => isAdmin || m.visibilita !== "utenti" || ((m.created_by || "").toLowerCase() === (user?.username || "").toLowerCase() && !!user?.username);

  const load = async () => {
    setLoading(true);
    try {
      const params = annoCorrente ? { anno: annoCorrente, escludi_openday: 1 } : { escludi_openday: 1 };
      const [r, m] = await Promise.all([
        api.get("/tabelle-config/modelli", { params }),
        api.get("/tabelle-config/meta"),
      ]);
      setItems(r.data.items || []);
      setMeta(m.data || { tipi: [], anagrafica_campi: [] });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [annoCorrente]);

  const nuovo = () => setEditing({ nome: "", codice: "", ordine: (items.length + 1) * 10, anno: annoCorrente, attivo: true, colonne: [] });

  const cid = () => Math.random().toString(36).slice(2, 10);
  const PRESETS = {
    idoneita: {
      nome: "Idoneità Sportiva", codice: "IDONEITA", ordine: 10, anno: annoCorrente, attivo: true,
      colonne: [
        { id: cid(), nome: "Classe", tipo: "anagrafica", campo: "classe" },
        { id: cid(), nome: "Certificato idoneità sportiva", tipo: "file", obbligatoria: true },
        { id: cid(), nome: "Scadenza certificato", tipo: "data" },
        { id: cid(), nome: "Certificato Esonero Sportivo", tipo: "file" },
      ],
    },
    farmaci: {
      nome: "Farmaci", codice: "FARMACI", ordine: 20, anno: annoCorrente, attivo: true,
      colonne: [
        { id: cid(), nome: "Classe", tipo: "anagrafica", campo: "classe" },
        { id: cid(), nome: "Data certificato medico", tipo: "data" },
        { id: cid(), nome: "PEC ricevuta", tipo: "spunta" },
        { id: cid(), nome: "Farmaco 1", tipo: "testo" },
        { id: cid(), nome: "Farmaco 2", tipo: "testo" },
        { id: cid(), nome: "Farmaco 3", tipo: "testo" },
        { id: cid(), nome: "Farmaco 4", tipo: "testo" },
        { id: cid(), nome: "Farmaco 5", tipo: "testo" },
        { id: cid(), nome: "Allegato certificato", tipo: "file" },
      ],
    },
  };
  const daPreset = (key) => { const p = PRESETS[key]; setEditing({ ...p, anno: annoCorrente, colonne: p.colonne.map(c => ({ ...c, id: cid() })) }); };

  const salva = async (m) => {
    try {
      if (m._id) await api.put(`/tabelle-config/modelli/${m._id}`, m);
      else await api.post("/tabelle-config/modelli", m);
      setEditing(null);
      await load();
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const elimina = async (m) => {
    if (!window.confirm(`Eliminare il modello "${m.nome}"?\n\nLa tabella può essere eliminata solo se non contiene registrazioni. Operazione irreversibile.`)) return;
    try { await api.delete(`/tabelle-config/modelli/${m._id}`); await load(); }
    catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const duplica = async (m) => {
    const nome = window.prompt(`Duplica il modello "${m.nome}".\n\nViene creata una copia della struttura (colonne, classi, impostazioni), senza i dati compilati.\n\nNome della nuova tabella:`, `${m.nome} (copia)`);
    if (nome === null) return;
    try { await api.post(`/tabelle-config/modelli/${m._id}/duplica`, { nome: nome.trim() || undefined }); await load(); }
    catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const migra = async (m, fonte) => {
    const nomeF = fonte === "idoneita" ? "Idoneità Sportiva" : "Farmaci";
    if (!window.confirm(`Migrare i dati "${nomeF}" dell'A.S. ${m.anno}/${String((m.anno + 1) % 100).padStart(2, "0")} dentro il modello "${m.nome}"?\n\nI dati originali NON vengono toccati. I file già presenti non vengono ricopiati.`)) return;
    try {
      const r = await api.post(`/tabelle-config/modelli/${m._id}/migra`, null, { params: { fonte, anno: m.anno } });
      const d = r.data;
      if (d.migrati > 0) {
        alert(`✅ Migrazione completata: ${d.migrati} valori importati nel modello "${m.nome}".`);
      } else {
        const diag = d.diagnostica || {};
        let msg = `⚠️ 0 valori importati nel modello "${m.nome}" (anno ${diag.anno_modello ?? m.anno}).\n\n`;
        if (fonte === "idoneita") {
          msg += `Colonne file associate: ${(diag.colonne_associate || []).join(", ") || "nessuna"}\n`;
          msg += `Certificati totali nell'archivio (tutte le annate): ${diag.certificati_totali_categorie ?? 0}\n`;
        } else {
          msg += `Farmaci totali nell'archivio (tutte le annate): ${diag.farmaci_totali_archivio ?? 0}\n`;
        }
        const anni = diag.anni_disponibili || [];
        msg += `Anni con dati disponibili: ${anni.length ? anni.join(", ") : "nessuno"}\n\n`;
        if (anni.length && !anni.includes(diag.anno_modello)) {
          msg += `👉 I dati sono di un altro anno. Crea il modello per l'anno ${anni[0]} (oppure cambia l'anno del modello) e riprova.`;
        } else {
          msg += `👉 Non risultano dati da migrare per questo anno.`;
        }
        alert(msg);
      }
    } catch (e) { alert("Errore migrazione: " + (e.response?.data?.detail || e.message)); }
  };

  if (editing) return <ModelloEditor model={editing} meta={meta} onCancel={() => setEditing(null)} onSave={salva} />;

  return (
    <div className="sw-card">
      <div className="sw-card-body">
        <div className="sw-filter-row" style={{ alignItems: "center", gap: 10 }}>
          <AnnoSelector testId="tc-anno-builder" />
          <span className="sw-filter-label" style={{ marginLeft: 8 }}>Categoria:</span>
          <select data-testid="tc-builder-filter-cat" className="sw-filter-select" value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">Tutte</option>
            {catList.map(c => <option key={c} value={c}>{c}</option>)}
            <option value="__none__">(Senza categoria)</option>
          </select>
          <button className="sw-filter-btn" data-testid="tc-nuovo-modello" onClick={nuovo}
            style={{ background: "#2e7d32", color: "white", fontWeight: 700, marginLeft: "auto" }}>
            + Nuovo modello
          </button>
        </div>
      </div>
      <table className="sw-table" style={{ width: "100%" }}>
        <thead className="thead-blue">
          <tr><th style={{ width: 40 }}>#</th><th>Nome modello</th><th style={{ width: 130 }}>Categoria</th><th style={{ width: 110 }}>Codice</th><th style={{ width: 70, textAlign: "center" }}>Anno</th><th style={{ width: 80, textAlign: "center" }}>Colonne</th><th style={{ width: 140 }}>Proprietario</th><th style={{ width: 60, textAlign: "center" }}>Attivo</th><th style={{ width: 280, textAlign: "center" }}>Azioni</th></tr>
        </thead>
        <tbody>
          {loading ? <tr><td colSpan={9} className="sw-loading">Caricamento…</td></tr>
            : shownItems.length === 0 ? <tr><td colSpan={9} className="sw-empty"><p>Nessun modello{filterCat ? " per questa categoria" : " per l'anno selezionato"}. Crea il primo con "+ Nuovo modello".</p></td></tr>
            : shownItems.map((m, i) => (
              <tr key={m._id} data-testid={`tc-modello-row-${m._id}`}>
                <td style={{ textAlign: "center", color: "#888" }}>{m.ordine ?? i}</td>
                <td style={{ fontWeight: 700 }}>{m.visibilita === "utenti" ? <span title="Visibile solo a utenti selezionati">🔒 </span> : null}{m.nome}</td>
                <td data-testid={`tc-cat-${m._id}`}>{m.categoria ? <span style={{ fontSize: 11, fontWeight: 700, color: "#00695c", background: "#e0f2f1", padding: "2px 8px", borderRadius: 10 }}>📂 {m.categoria}</span> : <span style={{ color: "#bbb", fontSize: 11 }}>—</span>}</td>
                <td style={{ fontFamily: "monospace", fontSize: 11 }}>{m.codice}</td>
                <td style={{ textAlign: "center" }}>{m.anno}/{String((m.anno + 1) % 100).padStart(2, "0")}</td>
                <td style={{ textAlign: "center" }}>{(m.colonne || []).length}</td>
                <td style={{ fontSize: 11, color: "#546e7a" }} data-testid={`tc-owner-${m._id}`}>{m.created_by || <span style={{ color: "#bbb" }}>—</span>}</td>
                <td style={{ textAlign: "center" }}>{m.attivo ? "✅" : "—"}</td>
                <td style={{ textAlign: "center" }}>
                  {canEdit(m) ? (
                    <>
                      <button className="sw-filter-btn" data-testid={`tc-edit-${m._id}`} onClick={() => setEditing(m)} style={{ marginRight: 6 }}>Modifica</button>
                      <button className="sw-filter-btn" data-testid={`tc-dup-${m._id}`} onClick={() => duplica(m)} style={{ marginRight: 6, background: "#00695c", color: "white" }}>Duplica</button>
                      <button className="sw-filter-btn" data-testid={`tc-del-${m._id}`} onClick={() => elimina(m)} style={{ background: "#c62828", color: "white" }}>Elimina</button>
                    </>
                  ) : (
                    <>
                      <button className="sw-filter-btn" data-testid={`tc-dup-${m._id}`} onClick={() => duplica(m)} style={{ marginRight: 6, background: "#00695c", color: "white" }}>Duplica</button>
                      <span style={{ fontSize: 11, color: "#90a4ae" }} title="Solo il creatore o un amministratore può modificare questa tabella riservata">🔒 solo {m.created_by || "creatore"}/admin</span>
                    </>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function ModelloEditor({ model, meta, onCancel, onSave }) {
  const [m, setM] = useState({ ...model, standalone: !!model.standalone, classi: model.classi || [], visibilita: model.visibilita || "tutti", utenti_ammessi: model.utenti_ammessi || [], categoria: model.categoria || "", colonne: (model.colonne || []).map(c => ({ ...c })) });
  const [classiOpt, setClassiOpt] = useState([]);
  const [usersOpt, setUsersOpt] = useState([]);
  const [categorieOpt, setCategorieOpt] = useState([]);
  const upd = (patch) => setM(s => ({ ...s, ...patch }));
  useEffect(() => {
    api.get(`/tabelle-config/utenti-lista`).then(r => setUsersOpt(r.data.items || [])).catch(() => setUsersOpt([]));
    api.get(`/tabelle-config/categorie`).then(r => setCategorieOpt((r.data.items || []).map(x => x.nome))).catch(() => setCategorieOpt([]));
  }, []);
  useEffect(() => {
    let ok = true;
    if (!m.anno) { setClassiOpt([]); return; }
    api.get(`/tabelle-config/classi`, { params: { anno: m.anno } })
      .then(r => { if (ok) setClassiOpt((r.data.items || []).map(x => `${x.settore}${x.classe}`)); })
      .catch(() => { if (ok) setClassiOpt([]); });
    return () => { ok = false; };
  }, [m.anno]);
  const updCol = (idx, patch) => setM(s => ({ ...s, colonne: s.colonne.map((c, i) => i === idx ? { ...c, ...patch } : c) }));
  const addCol = () => setM(s => ({ ...s, colonne: [...s.colonne, { id: Math.random().toString(36).slice(2, 10), nome: "", tipo: "testo", ordine: s.colonne.length, opzioni: [], campo: "classe" }] }));
  const delCol = (idx) => setM(s => ({ ...s, colonne: s.colonne.filter((_, i) => i !== idx) }));
  const moveCol = (idx, dir) => setM(s => {
    const arr = [...s.colonne]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return s;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...s, colonne: arr.map((c, i) => ({ ...c, ordine: i })) };
  });

  const lbl = { fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 3 };
  const inp = { padding: "6px 8px", border: "1px solid #bbb", borderRadius: 3, fontSize: 12, boxSizing: "border-box" };

  return (
    <div className="sw-card" data-testid="tc-modello-editor">
      <div className="sw-card-body">
        <h3 style={{ marginTop: 0, color: "#1565c0" }}>{m._id ? "Modifica modello" : "Nuovo modello"}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 90px 90px", gap: 10, marginBottom: 14 }}>
          <div><label style={lbl}>Nome modello *</label><input data-testid="tc-model-nome" style={{ ...inp, width: "100%" }} value={m.nome} onChange={e => upd({ nome: e.target.value })} placeholder="es. Colloqui genitori" /></div>
          <div><label style={lbl}>Codice</label><input data-testid="tc-model-codice" style={{ ...inp, width: "100%" }} value={m.codice} onChange={e => upd({ codice: e.target.value.toUpperCase() })} placeholder="auto" /></div>
          <div><label style={lbl}>Ordine</label><input type="number" style={{ ...inp, width: "100%" }} value={m.ordine} onChange={e => upd({ ordine: Number(e.target.value) })} /></div>
          <div><label style={lbl}>Anno</label><input type="number" style={{ ...inp, width: "100%" }} value={m.anno} onChange={e => upd({ anno: Number(e.target.value) })} /></div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>📂 Categoria <span style={{ fontWeight: 400, color: "#888" }}>(comune a tutti gli utenti — scegline una o scrivine una nuova)</span></label>
          <input data-testid="tc-model-categoria" list="tc-categorie-list" style={{ ...inp, width: 320, maxWidth: "100%" }} value={m.categoria} onChange={e => upd({ categoria: e.target.value })} placeholder="es. Sanitario, Segreteria, Sport…" />
          <datalist id="tc-categorie-list">
            {categorieOpt.map(c => <option key={c} value={c} />)}
          </datalist>
        </div>
        <label style={{ ...lbl, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={!!m.attivo} onChange={e => upd({ attivo: e.target.checked })} /> Attivo (visibile in Compilazione)
        </label>

        <div style={{ marginTop: 10, background: "#ede7f6", border: "1px solid #b39ddb", borderRadius: 6, padding: "10px 12px" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "#4527a0", cursor: "pointer" }}>
            <input type="checkbox" data-testid="tc-model-standalone" checked={!!m.standalone} onChange={e => upd({ standalone: e.target.checked })} />
            🧩 Tabella indipendente (righe libere, senza anagrafica alunni)
          </label>
          <div style={{ fontSize: 11, color: "#5e35b1", marginTop: 4 }}>
            Se attiva, la tabella non è legata agli alunni: inserisci righe libere con “➕ Aggiungi riga”, solo con le colonne personalizzate qui sotto (niente colonne Anagrafica né filtro classi).
          </div>
        </div>

        <div style={{ marginTop: 14, background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 6, padding: "10px 12px" }}>
          <label style={{ ...lbl, marginBottom: 6 }}>👥 Proprietario / Visibilità della tabella</label>
          <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
              <input type="radio" name="vis" data-testid="tc-vis-tutti" checked={m.visibilita !== "utenti"} onChange={() => upd({ visibilita: "tutti" })} />
              🌐 Tutti (la vedono tutti gli utenti)
            </label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
              <input type="radio" name="vis" data-testid="tc-vis-utenti" checked={m.visibilita === "utenti"} onChange={() => upd({ visibilita: "utenti" })} />
              🔒 Solo utenti selezionati
            </label>
            {m.visibilita === "utenti" && (
              <UtentiMultiSelect options={usersOpt} selected={m.utenti_ammessi || []} onChange={(v) => upd({ utenti_ammessi: v })} />
            )}
          </div>
          {m.visibilita === "utenti" && (
            <div style={{ fontSize: 11, color: "#607d8b", marginTop: 6 }}>
              Gli amministratori e il creatore della tabella la vedono sempre.
              {(m.utenti_ammessi || []).length === 0 ? " Nessun utente selezionato: solo tu e gli admin la vedrete." : ` Utenti ammessi: ${(m.utenti_ammessi || []).join(", ")}`}
            </div>
          )}
        </div>

        <div style={{ marginTop: 16, borderTop: "1px dashed #ccc", paddingTop: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <b style={{ fontSize: 13, color: "#37474f" }}>Colonne del modello</b>
            <button className="sw-filter-btn" data-testid="tc-add-colonna" onClick={addCol} style={{ background: "#00695c", color: "white", fontWeight: 700 }}>+ Aggiungi colonna</button>
          </div>
          {m.colonne.length === 0 && <div style={{ color: "#999", fontSize: 12, padding: 8 }}>Nessuna colonna. Aggiungi almeno una colonna.</div>}
          {(() => { const firstAnagIdx = m.colonne.findIndex(x => x.tipo === "anagrafica"); const optsOf = (c) => (c.opzioniText !== undefined ? c.opzioniText : (c.opzioni || []).join(", ")).split(",").map(s => s.trim()).filter(Boolean); return m.colonne.map((c, idx) => (
            <React.Fragment key={c.id}>
            <div data-testid={`tc-colonna-${idx}`} style={{ display: "grid", gridTemplateColumns: "24px 2fr 1.4fr 1.8fr 70px 46px", gap: 8, alignItems: "center", background: idx % 2 ? "#fafafa" : "#f1f8ff", padding: 8, borderRadius: 4, marginBottom: (c.tipo === "anagrafica" && idx === firstAnagIdx) || c.tipo === "lista" || c.tipo === "multi_lista" ? 0 : 6 }}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <button onClick={() => moveCol(idx, -1)} title="Su" style={{ border: "none", background: "none", cursor: "pointer", fontSize: 11 }}>▲</button>
                <button onClick={() => moveCol(idx, 1)} title="Giù" style={{ border: "none", background: "none", cursor: "pointer", fontSize: 11 }}>▼</button>
              </div>
              <input data-testid={`tc-col-nome-${idx}`} style={inp} value={c.nome} onChange={e => updCol(idx, { nome: e.target.value })} placeholder={`Colonna ${idx + 1}`} />
              <select data-testid={`tc-col-tipo-${idx}`} style={inp} value={c.tipo} onChange={e => updCol(idx, { tipo: e.target.value })}>
                {(meta.tipi || []).filter(t => !m.standalone || (t !== "anagrafica" && t !== "file")).map(t => <option key={t} value={t}>{TIPI_LABEL[t] || t}</option>)}
              </select>
              {c.tipo === "lista" || c.tipo === "multi_lista" ? (
                <input data-testid={`tc-col-opzioni-${idx}`} style={inp} value={c.opzioniText !== undefined ? c.opzioniText : (c.opzioni || []).join(", ")} onChange={e => updCol(idx, { opzioniText: e.target.value })} placeholder="Opzioni separate da virgola (es. xxxx, fff, sss)" />
              ) : c.tipo === "anagrafica" ? (
                <select data-testid={`tc-col-campo-${idx}`} style={inp} value={c.campo || "classe"} onChange={e => updCol(idx, { campo: e.target.value })}>
                  {(meta.anagrafica_campi || []).map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
                </select>
              ) : c.tipo === "spunta" ? (
                <label data-testid={`tc-col-evidenzia-${idx}`} title="Se spuntato nella tabella, evidenzia la riga con un ❗ rosso" style={{ fontSize: 10, fontWeight: 700, color: c.evidenzia ? "#d32f2f" : "#777", display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!c.evidenzia} onChange={e => updCol(idx, { evidenzia: e.target.checked })} /> ❗ Evidenzia riga
                </label>
              ) : <div style={{ fontSize: 10, color: "#999" }}>—</div>}
              <label title="Colonna obbligatoria: le celle vuote saranno evidenziate in rosso" style={{ fontSize: 10, fontWeight: 700, color: c.obbligatoria ? "#c62828" : "#777", display: "inline-flex", alignItems: "center", gap: 4, cursor: c.tipo === "anagrafica" ? "not-allowed" : "pointer" }}>
                <input type="checkbox" data-testid={`tc-col-obblig-${idx}`} disabled={c.tipo === "anagrafica"} checked={!!c.obbligatoria} onChange={e => updCol(idx, { obbligatoria: e.target.checked })} /> Obblig.
              </label>
              <button data-testid={`tc-col-del-${idx}`} onClick={() => delCol(idx)} style={{ background: "#c62828", color: "white", border: "none", borderRadius: 3, padding: "6px 0", cursor: "pointer", fontWeight: 700 }}>🗑</button>
            </div>
            {c.tipo === "anagrafica" && idx === firstAnagIdx && !m.standalone && (
              <div data-testid="tc-anagrafica-classi" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 4, padding: "8px 10px", marginBottom: 6, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#2e7d32" }}>🎯 Classi di questa tabella:</span>
                <ClassiMultiSelect options={classiOpt} selected={m.classi || []} onChange={(v) => upd({ classi: v })} />
                <span style={{ fontSize: 11, color: "#607d8b" }}>{(m.classi || []).length === 0 ? "Vuoto = tutti gli alunni. (es. E1A = settore E, classe 1A)" : `Apre solo: ${(m.classi || []).join(", ")}`}</span>
              </div>
            )}
            {(c.tipo === "lista" || c.tipo === "multi_lista") && (
              <div data-testid={`tc-lista-colori-${idx}`} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 4, padding: "8px 10px", marginBottom: 6, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#ef6c00" }}>🎨 Colore riga per valore:</span>
                {optsOf(c).length === 0 ? <i style={{ fontSize: 11, color: "#999" }}>Inserisci prima le opzioni qui sopra</i>
                  : optsOf(c).map(o => (
                    <label key={o} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, background: "white", border: "1px solid #eee", borderRadius: 4, padding: "2px 6px" }}>
                      <input type="color" data-testid={`tc-lista-colore-${idx}-${o}`} value={(c.colori || {})[o] || "#ffffff"} onChange={e => updCol(idx, { colori: { ...(c.colori || {}), [o]: e.target.value } })} style={{ width: 26, height: 20, border: "none", padding: 0, cursor: "pointer" }} />
                      <span>{o}</span>
                      {(c.colori || {})[o] && <button onClick={() => { const cc = { ...(c.colori || {}) }; delete cc[o]; updCol(idx, { colori: cc }); }} title="Rimuovi colore" style={{ border: "none", background: "none", cursor: "pointer", color: "#c62828", fontSize: 12 }}>✕</button>}
                    </label>
                  ))}
              </div>
            )}
            </React.Fragment>
          )); })()}
        </div>

        <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="sw-filter-btn" onClick={onCancel}>Annulla</button>
          <button className="sw-filter-btn" data-testid="tc-model-save" onClick={() => {
            if (!m.nome.trim()) { alert("Nome obbligatorio"); return; }
            const payload = {
              ...m,
              colonne: m.colonne.map(c => {
                const col = { ...c };
                if (col.tipo === "lista" || col.tipo === "multi_lista") {
                  const raw = col.opzioniText !== undefined ? col.opzioniText : (col.opzioni || []).join(", ");
                  col.opzioni = raw.split(",").map(s => s.trim()).filter(Boolean);
                }
                delete col.opzioniText;
                return col;
              }),
            };
            onSave(payload);
          }}
            style={{ background: "#1565c0", color: "white", fontWeight: 700 }}>💾 Salva modello</button>
        </div>
      </div>
    </div>
  );
}

/* ==================== GRIGLIA COMPILAZIONE ==================== */
function CompilaGrid() {
  const { annoCorrente } = useAuth();
  const [modelli, setModelli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAnno, setFilterAnno] = useState(annoCorrente || "");
  const [filterCat, setFilterCat] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/tabelle-config/modelli", { params: { solo_attivi: true, escludi_openday: 1 } });
      const list = (r.data.items || []).slice().sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "it"));
      setModelli(list);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const anni = useMemo(() => Array.from(new Set(modelli.map(m => m.anno))).sort((a, b) => b - a), [modelli]);
  const catList = useMemo(() => Array.from(new Set(modelli.map(m => m.categoria).filter(Boolean))).sort(), [modelli]);
  const shown = useMemo(() => modelli.filter(m => (!filterAnno || m.anno === Number(filterAnno)) && (!filterCat || (filterCat === "__none__" ? !m.categoria : m.categoria === filterCat))), [modelli, filterAnno, filterCat]);

  if (selected) return selected.standalone
    ? <StandaloneGrid modello={selected} onBack={() => setSelected(null)} />
    : <ModelloDataGrid modello={selected} onBack={() => setSelected(null)} />;

  return (
    <div className="sw-card" data-testid="tc-modelli-attivi-list">
      <div className="sw-card-body">
        <div className="sw-filter-row" style={{ alignItems: "center", gap: 10 }}>
          <span className="sw-filter-label">Anno scolastico:</span>
          <select data-testid="tc-list-anno" className="sw-filter-select" value={filterAnno} onChange={e => setFilterAnno(e.target.value)} style={{ minWidth: 130, fontWeight: 700 }}>
            <option value="">Tutti gli anni</option>
            {anni.map(a => <option key={a} value={a}>{a}/{String((a + 1) % 100).padStart(2, "0")}</option>)}
          </select>
          <span className="sw-filter-label" style={{ marginLeft: 8 }}>Categoria:</span>
          <select data-testid="tc-list-cat" className="sw-filter-select" value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">Tutte</option>
            {catList.map(c => <option key={c} value={c}>{c}</option>)}
            <option value="__none__">(Senza categoria)</option>
          </select>
          <span style={{ marginLeft: "auto", fontSize: 11, color: "#444" }}><b>{shown.length}</b> modelli attivi</span>
        </div>
      </div>
      <table className="sw-table" style={{ width: "100%" }}>
        <thead className="thead-blue">
          <tr><th>Nome modello</th><th style={{ width: 130 }}>Categoria</th><th style={{ width: 110 }}>Codice</th><th style={{ width: 80, textAlign: "center" }}>Anno</th><th style={{ width: 90, textAlign: "center" }}>Colonne</th><th style={{ width: 160, textAlign: "center" }}>Azioni</th></tr>
        </thead>
        <tbody>
          {loading ? <tr><td colSpan={6} className="sw-loading">Caricamento…</td></tr>
            : shown.length === 0 ? <tr><td colSpan={6} className="sw-empty"><p>Nessun modello attivo per i filtri selezionati. Crea o attiva un modello dalla tab "Modelli & colonne".</p></td></tr>
            : shown.map(m => (
              <tr key={m._id} data-testid={`tc-attivo-row-${m._id}`} style={{ cursor: "pointer" }} onClick={() => setSelected(m)}>
                <td style={{ fontWeight: 700, color: "#0d47a1" }}>{m.visibilita === "utenti" ? <span title="Visibile solo a utenti selezionati">🔒 </span> : null}🧩 {m.nome}</td>
                <td>{m.categoria ? <span style={{ fontSize: 11, fontWeight: 700, color: "#00695c", background: "#e0f2f1", padding: "2px 8px", borderRadius: 10 }}>📂 {m.categoria}</span> : <span style={{ color: "#bbb", fontSize: 11 }}>—</span>}</td>
                <td style={{ fontFamily: "monospace", fontSize: 11 }}>{m.codice}</td>
                <td style={{ textAlign: "center" }}>{m.anno}/{String((m.anno + 1) % 100).padStart(2, "0")}</td>
                <td style={{ textAlign: "center" }}>{(m.colonne || []).length}</td>
                <td style={{ textAlign: "center" }}>
                  <button className="sw-filter-btn" data-testid={`tc-apri-${m._id}`} onClick={(e) => { e.stopPropagation(); setSelected(m); }}
                    style={{ background: "#1565c0", color: "white", fontWeight: 700 }}>Apri tabella dati →</button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

export function StandaloneGrid({ modello, onBack, recordMode = false, anno: annoProp, titolo, toolbarRight = false }) {
  const modelloId = modello._id;
  const anno = annoProp != null ? annoProp : modello.anno;
  const [colonne, setColonne] = useState(modello.colonne || []);
  const [colVisibili, setColVisibili] = useState(modello.colonne_visibili || []);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filtroSezione, setFiltroSezione] = useState(""); // 🆕 filtro per Sezione (Secondaria)
  const [exporting, setExporting] = useState(false);
  const [mailTemplates, setMailTemplates] = useState([]);
  const [mailPlaceholders, setMailPlaceholders] = useState([]);
  const [mailMgrOpen, setMailMgrOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState(null); // {row, email, template}
  const [pickFor, setPickFor] = useState(null); // {row, email}
  const [recordModal, setRecordModal] = useState(null); // {row} | {row:null}
  const [storicoFor, setStoricoFor] = useState(null); // {row} per storico mail
  const cols = useMemo(() => (colonne || []).filter(c => c.tipo !== "anagrafica" && c.tipo !== "file"), [colonne]);
  const emailCol = useMemo(() => (cols || []).find(c => c.tipo === "email"), [cols]);
  // In modalità lista Iscrizioni (recordMode) mostriamo solo le colonne fino a Telefono + Conferma + Pagato.
  // Gli altri campi restano visibili/modificabili solo nel dettaglio (RecordModal).
  const listCols = useMemo(() => {
    if (!recordMode) return cols;
    // Se la tabella ha una selezione colonne salvata, usala; altrimenti default.
    if (colVisibili && colVisibili.length) {
      const vis = new Set(colVisibili.map(String));
      return cols.filter(c => vis.has(String(c.id)));
    }
    const VIS = new Set(["cognome e nome", "sesso", "data di nascita", "luogo di nascita", "codice fiscale", "nazionalita", "comune di residenza", "indirizzo di residenza", "mail", "telefono", "importo versato", "pagato", "conferma"]);
    const norm = s => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase().replace(/\s+/g, " ");
    return cols.filter(c => c.tipo === "pagato" || VIS.has(norm(c.nome)));
  }, [cols, recordMode, colVisibili]);

  // id colonne attualmente mostrate (base per il selettore "Colonne")
  const effectiveVisibleIds = useMemo(() => listCols.map(c => String(c.id)), [listCols]);
  const toggleColVisible = async (colId) => {
    const id = String(colId);
    const cur = new Set((colVisibili && colVisibili.length ? colVisibili.map(String) : effectiveVisibleIds));
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    const ordered = cols.map(c => String(c.id)).filter(cid => cur.has(cid));
    if (ordered.length === 0) { alert("Almeno una colonna deve restare visibile."); return; }
    setColVisibili(ordered);
    try { await api.put(`/tabelle-config/modelli/${modelloId}/colonne-visibili`, { colonne_visibili: ordered }); }
    catch (e) { alert("Errore salvataggio colonne: " + (e.response?.data?.detail || e.message)); load(); }
  };

  const normNome0 = s => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase().replace(/\s+/g, " ");
  const hasImporto = useMemo(() => cols.some(c => normNome0(c.nome) === "importo versato"), [cols]);
  // Colonna "Pagato" autonoma (modalità Cassa/POS/Bonifico) — guida lo sfondo rosso delle righe non pagate
  const pagatoCol = useMemo(() => cols.find(c => (c.tipo === "lista" || c.tipo === "spunta") && normNome0(c.nome) === "pagato"), [cols]);

  // 🆕 Sezione a scelta (solo tabelle con "Sezione" tipo lista, es. Secondaria): filtro + contatore iscritti
  const sezioneCol = useMemo(() => cols.find(c => normNome0(c.nome) === "sezione" && c.tipo === "lista" && (c.opzioni || []).length > 0), [cols]);
  const sezioneCounts = useMemo(() => {
    if (!sezioneCol) return null;
    const counts = {};
    (sezioneCol.opzioni || []).forEach(o => { counts[o] = 0; });
    let vuote = 0;
    rows.forEach(it => {
      const v = (it.valori?.[sezioneCol.id] || "").toString().trim();
      if (!v) { vuote += 1; return; }
      counts[v] = (counts[v] || 0) + 1;
    });
    return { counts, vuote, totale: rows.length };
  }, [sezioneCol, rows]);

  const loadMailTemplates = async () => {
    try {
      const r = await api.get(`/tabelle-config/modelli/${modelloId}/mail`);
      setMailTemplates(r.data.items || []);
      setMailPlaceholders(r.data.placeholders || []);
    } catch (e) { /* silenzioso */ }
  };
  useEffect(() => { loadMailTemplates(); }, [modelloId]);

  const emailOfRow = (it) => {
    if (emailCol) {
      const v = it.valori?.[emailCol.id];
      if (typeof v === "string" && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim())) return v.trim();
      return "";
    }
    return emailInRow(it) || "";
  };

  const apriInvio = (it) => {
    const email = emailOfRow(it);
    if (!email) { alert("Questa riga non ha un indirizzo email valido nel campo di tipo 'email'."); return; }
    if (!mailTemplates.length) { setMailMgrOpen(true); return; }
    if (mailTemplates.length === 1) { setSendTarget({ row: it, email, template: mailTemplates[0] }); return; }
    setPickFor({ row: it, email });
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tabelle-config/modelli/${modelloId}/matrice`, { params: { anno } });
      setColonne(r.data.colonne || []);
      setColVisibili(r.data.colonne_visibili || []);
      setRows(r.data.items || []);
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [modelloId, anno]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = rows;
    if (sezioneCol && filtroSezione) {
      if (filtroSezione === "__vuote__") base = base.filter(it => !((it.valori?.[sezioneCol.id] || "").toString().trim()));
      else base = base.filter(it => ((it.valori?.[sezioneCol.id] || "").toString().trim()) === filtroSezione);
    }
    if (!q) return base;
    return base.filter(it => cols.map(c => cellText(c, it.valori?.[c.id])).join(" ").toLowerCase().includes(q));
  }, [rows, cols, search, sezioneCol, filtroSezione]);

  const patchRow = (rid, patch) => setRows(rs => rs.map(r => r.row_id === rid ? { ...r, ...patch, valori: { ...r.valori, ...(patch.valori || {}) } } : r));

  const addRow = async () => {
    try { const r = await api.post(`/tabelle-config/modelli/${modelloId}/righe`, {}, { params: { anno } }); setRows(rs => [...rs, { row_id: r.data.row_id, valori: {} }]); }
    catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const delRow = async (rid) => {
    if (!window.confirm("Eliminare questa riga?")) return;
    try { await api.delete(`/tabelle-config/righe/${modelloId}/${rid}`, { params: { anno } }); setRows(rs => rs.filter(r => r.row_id !== rid)); }
    catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const saveCell = async (rid, col, valore) => {
    patchRow(rid, { valori: { [col.id]: valore } });
    try {
      const r = await api.put(`/tabelle-config/righe/${modelloId}/${rid}`, { colonna_id: col.id, valore }, { params: { anno } });
      patchRow(rid, { valori: { [col.id]: r.data.valore }, updated_at: r.data.updated_at, updated_by: r.data.updated_by });
    } catch (e) { alert("Errore salvataggio: " + (e.response?.data?.detail || e.message)); load(); }
  };

  const prefillFromAlunno = async (cod) => {
    const r = await api.get(`/tabelle-config/modelli/${modelloId}/precompila-alunno`, { params: { cod } });
    return r.data; // { alunno, valori, campi_precompilati }
  };

  const submitRecord = async (existingRow, vals) => {
    let rid = existingRow?.row_id;
    if (!rid) {
      const r = await api.post(`/tabelle-config/modelli/${modelloId}/righe`, {}, { params: { anno } });
      rid = r.data.row_id;
    }
    for (const c of cols) {
      if (c.tipo === "pagato") continue;
      const nv = vals[c.id] ?? (c.tipo === "spunta" ? false : (c.tipo === "multi_lista" ? [] : ""));
      const ov = existingRow?.valori?.[c.id] ?? (c.tipo === "spunta" ? false : (c.tipo === "multi_lista" ? [] : ""));
      if (JSON.stringify(nv) !== JSON.stringify(ov)) {
        await api.put(`/tabelle-config/righe/${modelloId}/${rid}`, { colonna_id: c.id, valore: nv }, { params: { anno } });
      }
    }
    await load();
  };
  const deleteRecord = async (rid) => {
    if (!window.confirm("Eliminare questo record?")) return false;
    try { await api.delete(`/tabelle-config/righe/${modelloId}/${rid}`, { params: { anno } }); setRows(rs => rs.filter(r => r.row_id !== rid)); return true; }
    catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); return false; }
  };

  const stampaRicevuta = async (it) => {
    try {
      const r = await api.post(`/tabelle-config/modelli/${modelloId}/righe/${it.row_id}/ricevuta`, {}, { params: { anno }, responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      load();
    } catch (e) {
      let msg = e.message;
      try { if (e.response?.data) msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* noop */ }
      alert("Errore ricevuta: " + msg);
    }
  };

  const inviaRicevutaEmail = async (it) => {
    const dest = emailOfRow(it);
    if (!dest) { alert("Nessun indirizzo email in questa riga."); return; }
    if (!window.confirm(`Inviare la ricevuta di iscrizione a ${dest}?`)) return;
    try {
      const r = await api.post(`/tabelle-config/modelli/${modelloId}/righe/${it.row_id}/ricevuta/invia-email`, {}, { params: { anno } });
      alert(`Ricevuta inviata a ${r.data?.email || dest}.`);
      load();
    } catch (e) {
      alert("Errore invio ricevuta: " + (e.response?.data?.detail || e.message));
    }
  };

  const esporta = async (formato) => {
    setExporting(true);
    try {
      const row_ids = filtered.map(r => r.row_id);
      const r = await api.post(`/tabelle-config/modelli/${modelloId}/export`, { formato, anno, row_ids }, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      if (formato === "pdf") { window.open(url, "_blank"); setTimeout(() => window.URL.revokeObjectURL(url), 60000); }
      else {
        const a = document.createElement("a"); a.href = url;
        const safe = (titolo || modello.nome || "tabella").replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 40) || "tabella";
        a.download = `${safe}_${anno}.xlsx`; document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      }
    } catch (e) { alert("Errore export: " + (e.response?.data?.detail || e.message)); }
    finally { setExporting(false); }
  };

  const emailInRow = (it) => {
    for (const c of cols) {
      const v = it.valori?.[c.id];
      if (typeof v === "string" && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim())) return v.trim();
    }
    return null;
  };

  const renderCell = (it, col) => {
    const v = it.valori?.[col.id];
    const cellInp = { width: "100%", padding: "4px 6px", fontSize: 11, border: "1px solid #cfd8dc", borderRadius: 3, boxSizing: "border-box" };
    const tid = `sc-cell-${it.row_id}-${col.id}`;
    switch (col.tipo) {
      case "pagato":
        return <PagatoBadge v={v} />;
      case "spunta":
        return <input type="checkbox" data-testid={tid} checked={!!v} onChange={e => saveCell(it.row_id, col, e.target.checked)} />;
      case "data":
        return <input type="date" data-testid={tid} style={cellInp} defaultValue={v || ""} onChange={e => saveCell(it.row_id, col, e.target.value)} />;
      case "numero":
        return <input type="number" step="any" data-testid={tid} style={cellInp} defaultValue={v === "" || v == null ? "" : v} onBlur={e => saveCell(it.row_id, col, e.target.value)} />;
      case "percentuale":
        return <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}><input type="number" step="any" data-testid={tid} style={{ ...cellInp, width: "auto", flex: 1 }} defaultValue={v === "" || v == null ? "" : v} onBlur={e => saveCell(it.row_id, col, e.target.value)} /><span style={{ fontSize: 11, color: "#607d8b", fontWeight: 700 }}>%</span></span>;
      case "testo_lungo":
        return <textarea data-testid={tid} rows={col.righe || 2} style={{ ...cellInp, resize: "vertical", minWidth: 160, fontFamily: "inherit" }} defaultValue={v || ""} onBlur={e => saveCell(it.row_id, col, e.target.value)} />;
      case "lista":
        return <select data-testid={tid} style={cellInp} value={v || ""} onChange={e => saveCell(it.row_id, col, e.target.value)}><option value="">—</option>{(col.opzioni || []).map(o => <option key={o} value={o}>{o}</option>)}</select>;
      case "multi_lista":
        return <MultiListaCell col={col} value={v} onChange={(nv) => saveCell(it.row_id, col, nv)} testid={tid} />;
      case "link":
        return <span style={{ display: "inline-flex", alignItems: "center", gap: 4, width: "100%" }}><input data-testid={tid} style={cellInp} defaultValue={v || ""} placeholder="https://…" onBlur={e => saveCell(it.row_id, col, e.target.value)} />{v ? <a href={v} target="_blank" rel="noreferrer" title="Apri">↗</a> : null}</span>;
      case "email":
        return <input type="email" data-testid={tid} style={cellInp} defaultValue={v || ""} placeholder="nome@dominio…" onBlur={e => saveCell(it.row_id, col, e.target.value)} />;
      default:
        return <input data-testid={tid} style={cellInp} defaultValue={v || ""} onBlur={e => saveCell(it.row_id, col, e.target.value)} />;
    }
  };

  const btn = { padding: "6px 12px", border: "none", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: "pointer" };
  const chip = (active, color) => ({
    padding: "5px 13px", borderRadius: 999, fontSize: 12.5, cursor: "pointer",
    border: `1.5px solid ${color}`, background: active ? color : "white",
    color: active ? "white" : color, fontWeight: 600, transition: "background .12s, color .12s",
  });

  return (
    <div data-testid="tc-standalone-grid">
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        {onBack && <button className="sw-filter-btn" data-testid="sc-back" onClick={onBack}>← Indietro</button>}
        <h3 style={{ margin: 0, color: "#4527a0" }}>🧩 {titolo || modello.nome} <span style={{ fontSize: 12, fontWeight: 400, color: "#888" }}>· {anno}/{String((anno + 1) % 100).padStart(2, "0")} · tabella indipendente</span></h3>
        <input data-testid="sc-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Cerca…" style={{ padding: "6px 10px", border: "1px solid #cfd8dc", fontSize: 12, borderRadius: 3, width: 200 }} />
        <button data-testid="sc-add" onClick={recordMode ? () => setRecordModal({ row: null }) : addRow} style={{ ...btn, background: "#2e7d32", color: "white", marginLeft: toolbarRight ? "auto" : undefined }}>➕ Aggiungi {recordMode ? "record" : "riga"}</button>
        <button data-testid="sc-export-xlsx" onClick={() => esporta("xlsx")} disabled={exporting || !rows.length} style={{ ...btn, background: "#1b7e3c", color: "white" }}>📊 Esporta Excel</button>
        <button data-testid="sc-export-pdf" onClick={() => esporta("pdf")} disabled={exporting || !rows.length} style={{ ...btn, background: "#455a64", color: "white" }}>🖨 PDF</button>
        {recordMode && cols.length > 0 && (
          <details data-testid="sc-colonne-picker" style={{ position: "relative" }}>
            <summary style={{ ...btn, background: "#00695c", color: "white", listStyle: "none", userSelect: "none" }}>🧩 Colonne</summary>
            <div style={{ position: "absolute", zIndex: 30, top: "115%", left: 0, minWidth: 250, maxHeight: 380, overflowY: "auto", background: "white", border: "1px solid #b0bec5", borderRadius: 8, boxShadow: "0 6px 20px rgba(0,0,0,0.18)", padding: 8 }}>
              <div style={{ fontSize: 11, color: "#607d8b", padding: "2px 6px 6px", borderBottom: "1px solid #eceff1", marginBottom: 4 }}>Spunta le colonne da mostrare nella lista (salvato per la tabella)</div>
              {cols.map(c => {
                const id = String(c.id);
                const checked = (colVisibili && colVisibili.length) ? colVisibili.map(String).includes(id) : effectiveVisibleIds.includes(id);
                return (
                  <label key={id} data-testid={`sc-colopt-${id}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", fontSize: 12.5, cursor: "pointer", borderRadius: 4 }}>
                    <input type="checkbox" data-testid={`sc-colchk-${id}`} checked={checked} onChange={() => toggleColVisible(c.id)} />
                    <span>{c.nome || c.id}</span>
                  </label>
                );
              })}
            </div>
          </details>
        )}
        {emailCol && (
          <button data-testid="sc-mail-mgr" onClick={() => setMailMgrOpen(true)} style={{ ...btn, background: "#6a1b9a", color: "white" }}
            title="Crea e gestisci i modelli mail. Il tasto 📧 su ogni riga invia al valore del campo email.">✉️ Modelli mail {mailTemplates.length ? `(${mailTemplates.length})` : ""}</button>
        )}
        <span style={{ marginLeft: toolbarRight ? 10 : "auto", fontSize: 12, color: "#607d8b" }}><b>{filtered.length}</b> righe{search ? ` (di ${rows.length})` : ""}</span>
      </div>

      {sezioneCol && sezioneCounts && (
        <div data-testid="sc-sezione-bar" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "0 0 12px", padding: "10px 12px", background: "#f3f0fb", border: "1px solid #d1c4e9", borderRadius: 8 }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: "#4527a0", letterSpacing: 0.3, marginRight: 2 }}>📋 Sezione:</span>
          <button data-testid="sc-sezione-all" onClick={() => setFiltroSezione("")} style={chip(filtroSezione === "", "#4527a0")}>
            Tutte&nbsp;<b>{sezioneCounts.totale}</b>
          </button>
          {(sezioneCol.opzioni || []).map(o => (
            <button key={o} data-testid={`sc-sezione-opt-${o.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setFiltroSezione(filtroSezione === o ? "" : o)} style={chip(filtroSezione === o, "#1565c0")}>
              {o}&nbsp;<b>{sezioneCounts.counts[o] || 0}</b>
            </button>
          ))}
          {sezioneCounts.vuote > 0 && (
            <button data-testid="sc-sezione-vuote" onClick={() => setFiltroSezione(filtroSezione === "__vuote__" ? "" : "__vuote__")} style={chip(filtroSezione === "__vuote__", "#90a4ae")}>
              Senza sezione&nbsp;<b>{sezioneCounts.vuote}</b>
            </button>
          )}
        </div>
      )}

      {cols.length === 0 && (
        <div style={{ padding: 20, background: "#fff3e0", border: "1px solid #ffe082", borderRadius: 6, color: "#8d6e00", fontSize: 13 }}>
          Questa tabella non ha ancora colonne personalizzate. Aggiungile da “🛠 Modelli & colonne”.
        </div>
      )}

      {cols.length > 0 && (
        <div style={{ border: "1px solid #cfd8dc", borderRadius: 4, overflow: "auto", maxHeight: "72vh" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%", minWidth: 600 }} data-testid="sc-table">
            <thead>
              <tr>
                <th style={{ position: "sticky", top: 0, background: "#4527a0", color: "white", padding: "6px 8px", width: 40 }}>#</th>
                {listCols.map(c => { const wide = ["cognome", "nome", "cognome e nome"].includes((c.nome || "").trim().toLowerCase()); return <th key={c.id} style={{ position: "sticky", top: 0, background: "#4527a0", color: "white", padding: "6px 8px", textAlign: "left", whiteSpace: wide ? "normal" : "nowrap", width: wide ? "99%" : undefined, minWidth: wide ? 240 : undefined }}>{c.nome}{c.obbligatoria ? " *" : ""}</th>; })}
                <th style={{ position: "sticky", top: 0, background: "#4527a0", color: "white", padding: "6px 8px", width: 70 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={listCols.length + 2} style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={listCols.length + 2} style={{ padding: 24, textAlign: "center", color: "#90a4ae" }}>{recordMode ? "Nessun record. Usa “➕ Aggiungi record”." : "Nessuna riga. Usa “➕ Aggiungi riga”."}</td></tr>}
              {!loading && filtered.map((it, i) => {
                const email = emailOfRow(it);
                const nonPagato = pagatoCol ? !it.valori?.[pagatoCol.id] : false;
                return (
                  <tr key={it.row_id} data-testid={`sc-row-${i}`} style={{ borderBottom: "1px solid #eceff1", background: nonPagato ? "#fdecec" : "white" }}>
                    <td style={{ padding: "3px 8px", textAlign: "center", color: "#90a4ae", fontFamily: "monospace" }}>{i + 1}</td>
                    {listCols.map(c => { const wide = ["cognome", "nome", "cognome e nome"].includes((c.nome || "").trim().toLowerCase());
                      if (!recordMode) return <td key={c.id} style={{ padding: "3px 6px", verticalAlign: "top" }}>{renderCell(it, c)}</td>;
                      const raw = it.valori?.[c.id];
                      const isConferma = c.tipo === "spunta" && (c.nome || "").trim().toLowerCase() === "conferma";
                      const isPagato = c.tipo === "pagato";
                      const isPagatoManuale = pagatoCol && c.id === pagatoCol.id;
                      if (isPagatoManuale) {
                        const pv = (raw || "").toString();
                        const opts = c.opzioni && c.opzioni.length ? c.opzioni : ["Cassa", "POS", "Bonifico"];
                        return <td key={c.id} data-testid={`sc-view-${i}-${c.id}`} onClick={e => e.stopPropagation()}
                          style={{ padding: "6px 8px", verticalAlign: "top", textAlign: "center" }}>
                          <select data-testid={`sc-pagato-${i}`} value={pv}
                            onChange={e => saveCell(it.row_id, c, e.target.value)}
                            title={pv ? `Pagato con ${pv} — cambia o svuota` : "Non pagato — scegli la modalità"}
                            style={{
                              padding: "5px 8px", fontSize: 12, fontWeight: 700, borderRadius: 4, cursor: "pointer",
                              border: pv ? "1px solid #1b5e20" : "1px solid #cfd8dc",
                              background: pv ? "#2e7d32" : "white",
                              color: pv ? "white" : "#1a1a1a",
                            }}>
                            <option value="" style={{ background: "white", color: "#1a1a1a" }}>—</option>
                            {opts.map(o => <option key={o} value={o} style={{ background: "white", color: "#1a1a1a" }}>{o}</option>)}
                          </select>
                        </td>;
                      }
                      return <td key={c.id} data-testid={`sc-view-${i}-${c.id}`} onClick={() => setRecordModal({ row: it })} title="Apri / modifica record"
                        style={{ padding: "7px 8px", verticalAlign: "top", cursor: "pointer", whiteSpace: wide ? "normal" : "pre-wrap", width: wide ? "99%" : undefined, maxWidth: wide ? "none" : 280, minWidth: wide ? 240 : undefined, color: "#1a1a1a" }}>
                        {isPagato ? <PagatoBadge v={raw} />
                          : isConferma ? (raw ? <ConfermatoBadge /> : <span style={{ color: "#cfd8dc" }}>—</span>)
                          : (cellText(c, raw) || <span style={{ color: "#cfd8dc" }}>—</span>)}
                      </td>; })}
                    <td style={{ padding: "3px 6px", textAlign: "center", whiteSpace: "nowrap" }}>
                      {recordMode && <button data-testid={`sc-edit-${i}`} onClick={() => setRecordModal({ row: it })} title="Apri / modifica record" style={{ background: "transparent", border: "none", color: "#1565c0", cursor: "pointer", fontSize: 14, marginRight: 6 }}>✏️</button>}
                      {recordMode && hasImporto && <button data-testid={`sc-ricevuta-${i}`} onClick={() => stampaRicevuta(it)} title="Stampa ricevuta di iscrizione" style={{ background: "transparent", border: "none", color: "#2e7d32", cursor: "pointer", fontSize: 15, marginRight: 6 }}>🧾</button>}
                      {recordMode && hasImporto && <button data-testid={`sc-ricevuta-mail-${i}`} onClick={() => inviaRicevutaEmail(it)} disabled={!email}
                          title={email ? `Invia la ricevuta via email a ${email}` : "Nessuna email in questa riga"}
                          style={{ background: "transparent", border: "none", color: "#2e7d32", cursor: email ? "pointer" : "not-allowed", opacity: email ? 1 : 0.35, fontSize: 15, marginRight: 6 }}>📩</button>}
                      {emailCol
                        ? <button data-testid={`sc-mail-send-${i}`} onClick={() => apriInvio(it)} disabled={!email}
                            title={email ? `Invia il modello mail a ${email}` : "Nessuna email nel campo email di questa riga"}
                            style={{ background: "transparent", border: "none", cursor: email ? "pointer" : "not-allowed", opacity: email ? 1 : 0.35, fontSize: 15, marginRight: 6 }}>📧</button>
                        : (email && <a data-testid={`sc-mail-${i}`} href={`mailto:${email}`} title={`Invia email a ${email}`} style={{ textDecoration: "none", fontSize: 15, marginRight: 6 }}>✉️</a>)}
                      {recordMode && (
                        <button data-testid={`sc-mailhist-${i}`} onClick={() => setStoricoFor(it)}
                          title={it.mail?.count ? `${it.mail.count} mail inviate — clic per lo storico` : "Nessuna mail inviata — clic per lo storico"}
                          style={{ border: "none", cursor: "pointer", marginRight: 6, borderRadius: 999, padding: "2px 8px", fontSize: 11, fontWeight: 700, background: it.mail?.count ? "#1d4ed8" : "#eceff1", color: it.mail?.count ? "white" : "#90a4ae" }}>
                          ✉ {it.mail?.count || 0}
                        </button>
                      )}
                      <button data-testid={`sc-del-${i}`} onClick={() => delRow(it.row_id)} title="Elimina riga" style={{ background: "transparent", border: "none", color: "#e53935", cursor: "pointer", fontSize: 14 }}>🗑</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {recordModal && (
        <RecordModal cols={cols} row={recordModal.row} modelloNome={modello.nome}
          onSubmit={(vals) => submitRecord(recordModal.row, vals)}
          onPrefill={recordMode ? prefillFromAlunno : null}
          onDelete={recordModal.row ? async () => { const ok = await deleteRecord(recordModal.row.row_id); if (ok) setRecordModal(null); } : null}
          onClose={() => setRecordModal(null)} />
      )}
      {mailMgrOpen && (
        <MailModelliModal modelloId={modelloId} modelloNome={modello.nome}
          placeholders={mailPlaceholders}
          onClose={() => { setMailMgrOpen(false); loadMailTemplates(); }} />
      )}
      {pickFor && (
        <MailPickerModal templates={mailTemplates} label={pickFor.email}
          onPick={(t) => { setSendTarget({ row: pickFor.row, email: pickFor.email, template: t }); setPickFor(null); }}
          onClose={() => setPickFor(null)} />
      )}
      {sendTarget && (
        <InviaMailRowModal modelloId={modelloId} anno={anno}
          template={sendTarget.template} rowId={sendTarget.row.row_id} email={sendTarget.email}
          onClose={() => { setSendTarget(null); load(); }} />
      )}
      {storicoFor && (
        <StoricoMailRigaModal modelloId={modelloId} row={storicoFor} onClose={() => setStoricoFor(null)} />
      )}
    </div>
  );
}

function StoricoMailRigaModal({ modelloId, row, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/tabelle-config/modelli/${modelloId}/righe/${row.row_id}/storico-mail`);
        if (alive) setItems(r.data.items || []);
      } catch { if (alive) setItems([]); }
    })();
    return () => { alive = false; };
  }, [modelloId, row]);
  const fmt = (iso) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d.getTime()) ? iso : d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); };
  const stMap = { sent: { t: "Inviata", c: "#2e7d32" }, sent_partial: { t: "Parziale", c: "#ef6c00" }, error: { t: "Errore", c: "#c62828" } };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div data-testid="storico-mail-riga-modal" onClick={e => e.stopPropagation()} style={{ background: "white", borderRadius: 8, width: "min(680px,92vw)", maxHeight: "82vh", overflow: "auto", boxShadow: "0 10px 40px rgba(0,0,0,.3)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #eceff1", display: "flex", alignItems: "center", position: "sticky", top: 0, background: "white" }}>
          <h3 style={{ margin: 0, color: "#4527a0", fontSize: 16 }}>✉️ Mail inviate all'iscritto</h3>
          <button data-testid="storico-mail-close" onClick={onClose} style={{ marginLeft: "auto", background: "transparent", border: "none", fontSize: 22, cursor: "pointer", color: "#90a4ae", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 16 }}>
          {items === null && <div style={{ color: "#888", textAlign: "center", padding: 20 }}>Caricamento…</div>}
          {items && items.length === 0 && <div data-testid="storico-mail-empty" style={{ color: "#90a4ae", textAlign: "center", padding: 24 }}>Nessuna mail inviata a questo iscritto.</div>}
          {items && items.map((it, k) => { const st = stMap[it.status] || { t: it.status || "—", c: "#607d8b" };
            return (
              <div key={it.id || k} data-testid={`storico-mail-item-${k}`} style={{ border: "1px solid #eceff1", borderRadius: 6, padding: "10px 12px", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, color: "#263238" }}>{it.oggetto || "(senza oggetto)"}</span>
                  <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: "white", background: st.c, borderRadius: 999, padding: "2px 8px" }}>{st.t}</span>
                </div>
                <div style={{ fontSize: 12, color: "#607d8b", marginTop: 4 }}>{fmt(it.created_at)}{it.created_by ? ` · ${it.created_by}` : ""}</div>
                <div style={{ fontSize: 12, color: "#455a64", marginTop: 2 }}>A: {(it.destinatari || []).join(", ") || "—"}</div>
                {it.allegati && it.allegati.length > 0 && <div style={{ fontSize: 11, color: "#90a4ae", marginTop: 2 }}>📎 {it.allegati.join(", ")}</div>}
                {it.errore && <div style={{ fontSize: 12, color: "#c62828", marginTop: 2 }}>{it.errore}</div>}
              </div>
            ); })}
        </div>
      </div>
    </div>
  );
}

function RecordModal({ cols, row, modelloNome, onSubmit, onDelete, onClose, onPrefill }) {
  const isNew = !row;
  const [vals, setVals] = useState(() => {
    const init = {};
    for (const c of cols) init[c.id] = row?.valori?.[c.id] ?? (c.tipo === "spunta" ? false : (c.tipo === "multi_lista" ? [] : ""));
    return init;
  });
  const [busy, setBusy] = useState(false);
  const set = (id, v) => setVals(s => ({ ...s, [id]: v }));

  // 🆕 Preleva da anagrafica: ricerca alunno già esistente e precompila i campi disponibili
  const [aluQ, setAluQ] = useState("");
  const [aluResults, setAluResults] = useState([]);
  const [aluOpen, setAluOpen] = useState(false);
  const [aluBusy, setAluBusy] = useState(false);
  const [prefilledInfo, setPrefilledInfo] = useState(null); // {nome, campi:[]}
  useEffect(() => {
    if (!onPrefill) return;
    const q = aluQ.trim();
    if (q.length < 2) { setAluResults([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      setAluBusy(true);
      try {
        const r = await api.get(`/rid/alunni-search`, { params: { q } });
        if (active) { setAluResults(r.data.items || []); setAluOpen(true); }
      } catch (e) { if (active) setAluResults([]); }
      finally { if (active) setAluBusy(false); }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [aluQ, onPrefill]);

  const scegliAlunno = async (alu) => {
    setAluOpen(false);
    setAluQ(`${alu.cognome} ${alu.nome}`);
    try {
      const data = await onPrefill(alu.cod);
      const valori = data.valori || {};
      setVals(s => ({ ...s, ...valori }));
      setPrefilledInfo({ nome: `${alu.cognome} ${alu.nome}`, campi: data.campi_precompilati || [] });
    } catch (e) {
      alert("Errore prelievo dati: " + (e.response?.data?.detail || e.message));
    }
  };

  const save = async () => {
    for (const c of cols) {
      if (!c.obbligatoria || c.tipo === "spunta") continue;
      const v = vals[c.id];
      if (v === "" || v == null || (Array.isArray(v) && !v.length)) { alert(`Il campo "${c.nome}" è obbligatorio.`); return; }
    }
    setBusy(true);
    try { await onSubmit(vals); onClose(); }
    catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const finp = { width: "100%", padding: "11px 13px", fontSize: 16, border: "1px solid #cfd8dc", borderRadius: 6, boxSizing: "border-box" };
  const field = (c) => {
    const v = vals[c.id];
    switch (c.tipo) {
      case "pagato": return <div data-testid={`rec-field-${c.id}`}><PagatoBadge v={v} /></div>;
      case "spunta": return <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 16, cursor: "pointer" }}><input data-testid={`rec-field-${c.id}`} type="checkbox" style={{ width: 18, height: 18 }} checked={!!v} onChange={e => set(c.id, e.target.checked)} /> Sì</label>;
      case "data": return <input data-testid={`rec-field-${c.id}`} type="date" style={finp} value={v || ""} onChange={e => set(c.id, e.target.value)} />;
      case "numero": return <input data-testid={`rec-field-${c.id}`} type="number" step="any" style={finp} value={v ?? ""} onChange={e => set(c.id, e.target.value)} />;
      case "percentuale": return <div style={{ display: "flex", alignItems: "center", gap: 6 }}><input data-testid={`rec-field-${c.id}`} type="number" step="any" style={finp} value={v ?? ""} onChange={e => set(c.id, e.target.value)} /><span style={{ fontWeight: 700, color: "#607d8b" }}>%</span></div>;
      case "testo_lungo": return <textarea data-testid={`rec-field-${c.id}`} rows={c.righe || 3} style={{ ...finp, resize: "vertical", fontFamily: "inherit" }} value={v || ""} onChange={e => set(c.id, e.target.value)} />;
      case "lista": {
        const isPag = normNome(c.nome) === "pagato";
        const selStyle = (isPag && v) ? { ...finp, background: "#2e7d32", color: "white", fontWeight: 700, border: "1px solid #1b5e20" } : finp;
        return <select data-testid={`rec-field-${c.id}`} style={selStyle} value={v || ""} onChange={e => set(c.id, e.target.value)}><option value="" style={{ background: "white", color: "#1a1a1a" }}>—</option>{(c.opzioni || []).map(o => <option key={o} value={o} style={{ background: "white", color: "#1a1a1a" }}>{o}</option>)}</select>;
      }
      case "multi_lista": return <MultiListaCell col={c} value={v} onChange={nv => set(c.id, nv)} testid={`rec-field-${c.id}`} />;
      case "link": return <input data-testid={`rec-field-${c.id}`} style={finp} placeholder="https://…" value={v || ""} onChange={e => set(c.id, e.target.value)} />;
      case "email": return <input data-testid={`rec-field-${c.id}`} type="email" style={finp} placeholder="nome@dominio…" value={v || ""} onChange={e => set(c.id, e.target.value)} />;
      default: return <input data-testid={`rec-field-${c.id}`} style={finp} value={v || ""} onChange={e => set(c.id, e.target.value)} />;
    }
  };

  const normNome = s => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase().replace(/\s+/g, " ");
  const HALF_FIELDS = new Set(["data di nascita", "luogo di nascita", "comune di residenza", "indirizzo di residenza", "mail", "telefono"]);
  const isHalf = (c) => c.tipo === "spunta" || HALF_FIELDS.has(normNome(c.nome));

  return (
    <div onMouseDown={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 5000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflow: "auto" }}>
      <div data-testid="record-modal" onMouseDown={e => e.stopPropagation()} style={{ background: "white", borderRadius: 10, width: "100%", maxWidth: 960, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #eceff1", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0, color: "#4527a0", fontSize: 20 }}>{isNew ? "➕ Nuovo record" : "✏️ Modifica record"}</h3>
            {modelloNome && <div style={{ fontSize: 13, color: "#90a4ae", marginTop: 2 }}>{modelloNome}</div>}
          </div>
          <button data-testid="record-close" onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 26, lineHeight: 1, cursor: "pointer", color: "#90a4ae" }}>×</button>
        </div>
        {onPrefill && isNew && (
          <div style={{ padding: "14px 24px", borderBottom: "1px solid #eceff1", background: "#f3e5f5" }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 800, marginBottom: 6, color: "#6a1b9a" }}>
              👤 Preleva da anagrafica <span style={{ fontWeight: 500, color: "#8e24aa" }}>— iscrivi un alunno già presente, i dati disponibili verranno precompilati</span>
            </label>
            <div style={{ position: "relative", maxWidth: 520 }}>
              <input data-testid="rec-anagrafica-search" value={aluQ}
                onChange={e => { setAluQ(e.target.value); setPrefilledInfo(null); }}
                onFocus={() => aluResults.length && setAluOpen(true)}
                placeholder="Cerca per cognome, nome o codice fiscale…"
                style={{ width: "100%", padding: "10px 12px", fontSize: 15, border: "1px solid #ce93d8", borderRadius: 6, boxSizing: "border-box" }} />
              {aluBusy && <span style={{ position: "absolute", right: 12, top: 11, fontSize: 12, color: "#8e24aa" }}>…</span>}
              {aluOpen && aluResults.length > 0 && (
                <div data-testid="rec-anagrafica-results" style={{ position: "absolute", zIndex: 10, left: 0, right: 0, background: "white", border: "1px solid #ce93d8", borderTop: "none", borderRadius: "0 0 6px 6px", maxHeight: 260, overflowY: "auto", boxShadow: "0 8px 20px rgba(0,0,0,0.12)" }}>
                  {aluResults.map(alu => (
                    <div key={alu.cod} data-testid={`rec-anagrafica-opt-${alu.cod}`} onClick={() => scegliAlunno(alu)}
                      style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f3e5f5", fontSize: 14 }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f3e5f5"}
                      onMouseLeave={e => e.currentTarget.style.background = "white"}>
                      <b>{alu.cognome} {alu.nome}</b>
                      <span style={{ color: "#90a4ae", fontSize: 12, marginLeft: 8 }}>
                        {alu.codice_fiscale}{alu.classe ? ` · ${alu.settore ? alu.settore + " " : ""}${alu.classe}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {prefilledInfo && (
              <div data-testid="rec-anagrafica-ok" style={{ marginTop: 8, fontSize: 13, color: "#1b5e20", fontWeight: 600 }}>
                ✓ Dati di <b>{prefilledInfo.nome}</b> precompilati{prefilledInfo.campi.length ? `: ${prefilledInfo.campi.join(", ")}` : " (nessun campo disponibile da precompilare)"}. Completa i campi mancanti e salva.
              </div>
            )}
          </div>
        )}
        <div style={{ padding: 24, maxHeight: "68vh", overflow: "auto", display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "18px 24px", gridAutoFlow: "dense", alignItems: "end" }}>
          {cols.length === 0 && <div style={{ color: "#90a4ae", gridColumn: "1 / -1" }}>Questa tabella non ha colonne.</div>}
          {cols.map(c => (
            <div key={c.id} style={{ gridColumn: isHalf(c) ? "auto" : "1 / -1" }}>
              <label style={{ display: "block", fontSize: 14, fontWeight: 700, marginBottom: 5, color: "#37474f" }}>{c.nome}{c.obbligatoria ? " *" : ""}</label>
              {field(c)}
            </div>
          ))}
        </div>
        <div style={{ padding: "14px 20px", borderTop: "1px solid #eceff1", display: "flex", gap: 10, alignItems: "center" }}>
          {onDelete && <button data-testid="record-delete" onClick={onDelete} disabled={busy} style={{ padding: "9px 16px", border: "none", borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: "pointer", background: "#ffebee", color: "#c62828" }}>🗑 Elimina</button>}
          <span style={{ marginLeft: "auto" }} />
          <button data-testid="record-cancel" onClick={onClose} disabled={busy} style={{ padding: "9px 16px", border: "1px solid #cfd8dc", borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: "pointer", background: "white", color: "#546e7a" }}>Annulla</button>
          <button data-testid="record-save" onClick={save} disabled={busy} style={{ padding: "9px 18px", border: "none", borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: busy ? "default" : "pointer", background: busy ? "#90a4ae" : "#1b5e20", color: "white" }}>{busy ? "Salvataggio…" : "✓ Salva"}</button>
        </div>
      </div>
    </div>
  );
}

export function ModelloDataGrid({ modello, onBack }) {
  const modelloId = modello._id;
  const modelAnno = modello.anno;
  const [data, setData] = useState({ colonne: [], items: [], anno: modelAnno });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterSet, setFilterSet] = useState("");
  const [filterCla, setFilterCla] = useState(modello.classi || []);
  const [soloConDati, setSoloConDati] = useState(false);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState(1);
  const [exporting, setExporting] = useState(false);
  const fileTarget = useRef(null);
  const fileInputRef = useRef(null);
  const [mailTemplates, setMailTemplates] = useState([]);
  const [mailPlaceholders, setMailPlaceholders] = useState([]);
  const [mailMgrOpen, setMailMgrOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [storicoOpen, setStoricoOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState(null); // {it, template}
  const [pickFor, setPickFor] = useState(null); // alunno per cui scegliere il modello mail

  const BASE = process.env.REACT_APP_BACKEND_URL;

  const loadMailTemplates = async () => {
    try {
      const r = await api.get(`/tabelle-config/modelli/${modelloId}/mail`);
      setMailTemplates(r.data.items || []);
      setMailPlaceholders(r.data.placeholders || []);
    } catch (e) { /* silenzioso */ }
  };
  useEffect(() => { loadMailTemplates(); }, [modelloId]);

  const apriInvio = (it) => {
    if (!mailTemplates.length) { setMailMgrOpen(true); return; }
    if (mailTemplates.length === 1) { setSendTarget({ it, template: mailTemplates[0] }); return; }
    setPickFor(it);
  };

  const loadMatrice = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tabelle-config/modelli/${modelloId}/matrice`, { params: { anno: modelAnno } });
      setData(r.data);
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadMatrice(); }, [modelloId]);

  const setoriList = useMemo(() => Array.from(new Set(data.items.map(i => i.settore).filter(Boolean))).sort(), [data.items]);
  const classiList = useMemo(() => Array.from(new Set(data.items.filter(i => !filterSet || i.settore === filterSet).map(i => `${i.settore || ""}${i.classe || ""}`).filter(Boolean))).sort(), [data.items, filterSet]);
  const listaColorCols = useMemo(() => (data.colonne || []).filter(c => (c.tipo === "lista" || c.tipo === "multi_lista") && c.colori && Object.keys(c.colori).length), [data.colonne]);
  const spuntaFlagCols = useMemo(() => (data.colonne || []).filter(c => c.tipo === "spunta" && c.evidenzia), [data.colonne]);
  const rowColor = (it) => {
    for (const c of listaColorCols) {
      const v = it.valori?.[c.id];
      if (c.tipo === "multi_lista") { if (Array.isArray(v)) { for (const item of v) { if (c.colori[item]) return c.colori[item]; } } }
      else if (v && c.colori[v]) return c.colori[v];
    }
    return "";
  };
  const rowFlag = (it) => spuntaFlagCols.some(c => !!it.valori?.[c.id]);

  const hasData = (it) => (data.colonne || []).some(c => c.tipo !== "anagrafica" && cellText(c, it.valori?.[c.id]) !== "");
  const rowText = (it) => `${it.Cog} ${it.Nom} ${it.Cod} ${it.settore} ${it.classe} ` + (data.colonne || []).map(c => cellText(c, it.valori?.[c.id])).join(" ");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = data.items.filter(it => {
      if (filterSet && it.settore !== filterSet) return false;
      if (filterCla.length && !filterCla.includes(`${it.settore || ""}${it.classe || ""}`)) return false;
      if (soloConDati && !hasData(it)) return false;
      if (q && !rowText(it).toLowerCase().includes(q)) return false;
      return true;
    });
    if (sortCol) {
      arr = [...arr].sort((a, b) => {
        let va, vb, col = null;
        if (sortCol === "__nome") { va = `${a.Cog} ${a.Nom}`; vb = `${b.Cog} ${b.Nom}`; }
        else if (sortCol === "__classe") { va = a.classe; vb = b.classe; }
        else if (sortCol === "__cod") { return (a.Cod - b.Cod) * sortDir; }
        else {
          col = (data.colonne || []).find(x => x.id === sortCol);
          va = cellText(col, a.valori?.[sortCol]); vb = cellText(col, b.valori?.[sortCol]);
          if (col && (col.tipo === "numero" || col.tipo === "percentuale")) return ((parseFloat(va) || 0) - (parseFloat(vb) || 0)) * sortDir;
        }
        return String(va || "").localeCompare(String(vb || ""), "it", { numeric: true }) * sortDir;
      });
    }
    return arr;
  }, [data.items, data.colonne, search, filterSet, filterCla, soloConDati, sortCol, sortDir]);

  const esportaLista = async (formato) => {
    setExporting(true);
    try {
      const cods = filtered.map(it => it.Cod);
      const r = await api.post(
        `/tabelle-config/modelli/${modelloId}/export`,
        { formato, anno: modelAnno, cods },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(r.data);
      if (formato === "pdf") {
        window.open(url, "_blank");
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else {
        const a = document.createElement("a");
        a.href = url;
        const safe = (modello.nome || "tabella").replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 40) || "tabella";
        a.download = `${safe}_${modelAnno}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      }
    } catch (e) {
      alert("Errore export: " + (e.response?.data?.detail || e.message));
    } finally {
      setExporting(false);
    }
  };

  const toggleSort = (key) => {
    if (sortCol === key) { setSortDir(d => -d); }
    else { setSortCol(key); setSortDir(1); }
  };
  const sortArrow = (key) => sortCol === key ? (sortDir === 1 ? " ▲" : " ▼") : "";

  const patchRow = (cod, patch) => setData(d => ({ ...d, items: d.items.map(it => it.Cod === cod ? { ...it, ...patch, valori: { ...it.valori, ...(patch.valori || {}) } } : it) }));

  const saveCell = async (cod, col, valore) => {
    // Aggiornamento ottimistico (evita flicker su checkbox/select)
    patchRow(cod, { valori: { [col.id]: valore } });
    try {
      const params = { anno: modelAnno };
      const r = await api.put(`/tabelle-config/dati/${modelloId}/${cod}`, { colonna_id: col.id, valore }, { params });
      const patch = { valori: { [col.id]: r.data.valore } };
      if (r.data.updated_at) { patch.updated_at = r.data.updated_at; patch.updated_by = r.data.updated_by; }
      patchRow(cod, patch);
    } catch (e) { alert("Errore salvataggio: " + (e.response?.data?.detail || e.message)); loadMatrice(); }
  };

  const pickFile = (cod, col) => { fileTarget.current = { cod, col }; fileInputRef.current?.click(); };
  const onFileChosen = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f || !fileTarget.current) return;
    const { cod, col } = fileTarget.current;
    const fd = new FormData(); fd.append("file", f);
    try {
      const params = { anno: modelAnno };
      const r = await api.post(`/tabelle-config/dati/${modelloId}/${cod}/file/${col.id}`, fd, { params, headers: { "Content-Type": "multipart/form-data" } });
      patchRow(cod, { valori: { [col.id]: r.data.valore }, updated_at: r.data.updated_at, updated_by: r.data.updated_by });
    } catch (err) { alert("Errore upload: " + (err.response?.data?.detail || err.message)); }
  };
  const delFile = async (cod, col) => {
    if (!window.confirm("Rimuovere il file?")) return;
    const params = { anno: modelAnno };
    await api.delete(`/tabelle-config/dati/${modelloId}/${cod}/file/${col.id}`, { params });
    patchRow(cod, { valori: { [col.id]: null } });
  };

  const renderCell = (it, col) => {
    const v = it.valori?.[col.id];
    const cellInp = { width: "100%", padding: "4px 6px", fontSize: 11, border: "1px solid #cfd8dc", borderRadius: 3, boxSizing: "border-box" };
    switch (col.tipo) {
      case "pagato":
        return <PagatoBadge v={v} />;
      case "anagrafica":
        return <div style={{ fontSize: 11, color: "#37474f", padding: "2px 4px" }}>{v || "—"}</div>;
      case "spunta":
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <input type="checkbox" data-testid={`tc-cell-${it.Cod}-${col.id}`} checked={!!v} onChange={e => saveCell(it.Cod, col, e.target.checked)} />
            {col.evidenzia && v ? <span style={{ color: "#d32f2f", fontWeight: 900, fontSize: 14 }} title="Riga evidenziata">❗</span> : null}
          </span>
        );
      case "data":
        return <input type="date" data-testid={`tc-cell-${it.Cod}-${col.id}`} style={cellInp} defaultValue={v || ""} onChange={e => saveCell(it.Cod, col, e.target.value)} />;
      case "numero":
        return <input type="number" step="any" data-testid={`tc-cell-${it.Cod}-${col.id}`} style={cellInp} defaultValue={v === "" || v == null ? "" : v} onBlur={e => saveCell(it.Cod, col, e.target.value)} />;
      case "percentuale":
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 2, width: "100%" }}>
            <input type="number" step="any" data-testid={`tc-cell-${it.Cod}-${col.id}`} style={{ ...cellInp, width: "auto", flex: 1 }} defaultValue={v === "" || v == null ? "" : v} onBlur={e => saveCell(it.Cod, col, e.target.value)} />
            <span style={{ fontSize: 11, color: "#607d8b", fontWeight: 700 }}>%</span>
          </span>
        );
      case "testo_lungo":
        return <textarea data-testid={`tc-cell-${it.Cod}-${col.id}`} rows={col.righe || 2} style={{ ...cellInp, resize: "vertical", minWidth: 160, fontFamily: "inherit" }} defaultValue={v || ""} onBlur={e => saveCell(it.Cod, col, e.target.value)} />;
      case "link":
        return (
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <input type="url" data-testid={`tc-cell-${it.Cod}-${col.id}`} style={cellInp} defaultValue={v || ""} placeholder="https://…" onBlur={e => saveCell(it.Cod, col, e.target.value)} />
            {v ? <a href={/^https?:\/\//i.test(v) ? v : `https://${v}`} target="_blank" rel="noreferrer" title="Apri link" style={{ textDecoration: "none", fontSize: 13 }}>🔗</a> : null}
          </span>
        );
      case "multi_lista":
        return <MultiListaCell col={col} value={v} onChange={(nv) => saveCell(it.Cod, col, nv)} testid={`tc-cell-${it.Cod}-${col.id}`} />;
      case "lista":
        return (
          <select data-testid={`tc-cell-${it.Cod}-${col.id}`} style={cellInp} value={v || ""} onChange={e => saveCell(it.Cod, col, e.target.value)}>
            <option value="">—</option>
            {(col.opzioni || []).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      case "file":
        return v && v.file_id ? (
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <a href={`${BASE}/api/tabelle-config/dati/${modelloId}/${it.Cod}/file/${col.id}?anno=${data.anno || ""}`} target="_blank" rel="noreferrer"
              style={{ fontSize: 10, color: "#1565c0", fontWeight: 700, textDecoration: "none", maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={v.filename}>📎 {v.filename}</a>
            <button onClick={() => delFile(it.Cod, col)} title="Rimuovi" style={{ border: "none", background: "none", cursor: "pointer", color: "#c62828" }}>✕</button>
          </div>
        ) : (
          <button data-testid={`tc-cell-${it.Cod}-${col.id}`} onClick={() => pickFile(it.Cod, col)} style={{ fontSize: 10, background: "#eceff1", border: "1px dashed #90a4ae", borderRadius: 3, padding: "3px 8px", cursor: "pointer" }}>⬆ Carica</button>
        );
      default:
        return <input type="text" data-testid={`tc-cell-${it.Cod}-${col.id}`} style={cellInp} defaultValue={v || ""} onBlur={e => saveCell(it.Cod, col, e.target.value)} />;
    }
  };

  return (
    <div>
      <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={onFileChosen} />
      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "0 0 8px" }}>
        {onBack && <button data-testid="tc-back-list" onClick={onBack} className="sw-filter-btn" style={{ fontWeight: 700 }}>← Modelli attivi</button>}
        <div style={{ fontSize: 16, fontWeight: 800, color: "#0d47a1" }}>🧩 {modello.nome}</div>
        <div style={{ fontSize: 12, color: "#607d8b" }}>A.S. {modelAnno}/{String((modelAnno + 1) % 100).padStart(2, "0")}</div>
      </div>
      <div className="sw-card">
        <div className="sw-card-body">
          <div className="sw-filter-row" style={{ flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <span className="sw-filter-label">Cerca (tutto):</span>
            <input data-testid="tc-search" className="sw-filter-input" style={{ width: 200 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="testo in qualunque colonna…" />
            <label data-testid="tc-solo-con-dati" title="Mostra solo alunni con almeno un dato compilato" style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: soloConDati ? "#2e7d32" : "#555", display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
              <input type="checkbox" checked={soloConDati} onChange={e => setSoloConDati(e.target.checked)} /> Solo con dati
            </label>
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Settore:</span>
            <select data-testid="tc-filter-settore" className="sw-filter-select" value={filterSet} onChange={e => { setFilterSet(e.target.value); setFilterCla([]); }} style={{ minWidth: 90 }}>
              <option value="">Tutti</option>{setoriList.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Classe:</span>
            <ClassiMultiSelect options={classiList} selected={filterCla} onChange={setFilterCla} />
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#444" }}><b>{filtered.length}</b> alunni</span>
            <button data-testid="tc-export-pdf" onClick={() => esportaLista("pdf")} disabled={exporting}
              className="sw-filter-btn" style={{ background: "#c62828", color: "white", fontWeight: 700 }}>📄 PDF</button>
            <button data-testid="tc-export-xlsx" onClick={() => esportaLista("xlsx")} disabled={exporting}
              className="sw-filter-btn" style={{ background: "#2e7d32", color: "white", fontWeight: 700 }}>📊 Excel</button>
            <button data-testid="tc-mail-mgr" onClick={() => setMailMgrOpen(true)}
              className="sw-filter-btn" style={{ background: "#6a1b9a", color: "white", fontWeight: 700 }}
              title="Crea e gestisci i modelli mail da inviare agli alunni di questa tabella">✉️ Modelli mail {mailTemplates.length ? `(${mailTemplates.length})` : ""}</button>
            <button data-testid="tc-mail-bulk" onClick={() => { if (!mailTemplates.length) { setMailMgrOpen(true); return; } setBulkOpen(true); }}
              className="sw-filter-btn" style={{ background: "#ad1457", color: "white", fontWeight: 700 }}
              title="Invia un modello mail a tutti gli alunni attualmente filtrati">📤 Invio massivo</button>
            <button data-testid="tc-mail-storico" onClick={() => setStoricoOpen(true)}
              className="sw-filter-btn" style={{ background: "#455a64", color: "white", fontWeight: 700 }}
              title="Storico delle mail inviate da questa tabella">🕓 Storico invii</button>
          </div>
        </div>
      </div>

      {listaColorCols.length > 0 && (
        <div data-testid="tc-legenda-colori" className="sw-card" style={{ marginTop: 8 }}>
          <div className="sw-card-body" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, padding: "8px 12px" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#ef6c00" }}>🎨 Legenda colori:</span>
            {listaColorCols.map(c => (
              <span key={c.id} style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#37474f" }}>{c.nome}:</span>
                {Object.entries(c.colori).map(([val, col]) => (
                  <span key={val} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "#333" }}>
                    <span style={{ width: 16, height: 16, borderRadius: 3, background: col, border: "1px solid #cfd8dc", display: "inline-block" }} />
                    {val}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="sw-card" style={{ marginTop: 8 }}>
        <div style={{ overflow: "auto", maxHeight: "72vh" }}>
          <table className="sw-table" style={{ width: "100%" }}>
            <thead className="thead-blue">
              <tr>
                <th style={{ width: 52, textAlign: "center", cursor: "pointer" }} onClick={() => toggleSort("__cod")} data-testid="tc-sort-cod">Cod{sortArrow("__cod")}</th>
                <th style={{ minWidth: 150, cursor: "pointer" }} onClick={() => toggleSort("__nome")} data-testid="tc-sort-nome">Cognome Nome{sortArrow("__nome")}</th>
                <th style={{ width: 34, textAlign: "center" }}>S.</th>
                <th style={{ width: 34, textAlign: "center", cursor: "pointer" }} onClick={() => toggleSort("__classe")}>Cl.{sortArrow("__classe")}</th>
                {(data.colonne || []).map(c => (
                  <th key={c.id} style={{ minWidth: 110, cursor: "pointer" }} onClick={() => toggleSort(c.id)} data-testid={`tc-sort-${c.id}`} title={`${TIPI_LABEL[c.tipo]}${c.obbligatoria ? " · obbligatoria" : ""} — clic per ordinare`}>
                    {c.nome}{c.obbligatoria ? " *" : ""} <span style={{ fontSize: 9, opacity: 0.7 }}>({TIPI_LABEL[c.tipo]?.split(" ")[0]})</span>{sortArrow(c.id)}
                  </th>
                ))}
                <th style={{ width: 130 }}>Aggiornato</th>
                <th style={{ width: 44, textAlign: "center" }} title="Invia mail">✉️</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6 + (data.colonne?.length || 0)} className="sw-loading">Caricamento…</td></tr>
                : filtered.length === 0 ? <tr><td colSpan={6 + (data.colonne?.length || 0)} className="sw-empty"><p>Nessun alunno.</p></td></tr>
                : filtered.map(it => {
                  const rc = rowColor(it); const rf = rowFlag(it);
                  return (
                  <tr key={it.Cod} data-testid={`tc-row-${it.Cod}`} style={{ background: rc || undefined, boxShadow: rf ? "inset 4px 0 0 #d32f2f" : undefined }}>
                    <td style={{ textAlign: "center", fontFamily: "monospace", fontSize: 11 }}>{it.Cod}</td>
                    <td style={{ fontWeight: 600, fontSize: 12 }}>{it.Cog} {it.Nom}</td>
                    <td style={{ textAlign: "center", fontSize: 11 }}>{it.settore || "-"}</td>
                    <td style={{ textAlign: "center", fontSize: 11 }}>{it.classe || "-"}</td>
                    {(data.colonne || []).map(c => {
                      const v = it.valori?.[c.id];
                      const empty = c.tipo === "file" ? !(v && v.file_id) : (c.tipo === "spunta" ? !v : (c.tipo === "multi_lista" ? !(Array.isArray(v) && v.length) : (v === "" || v == null)));
                      const missing = c.obbligatoria && empty && c.tipo !== "anagrafica";
                      return <td key={c.id} data-testid={missing ? `tc-missing-${it.Cod}-${c.id}` : undefined}
                        style={missing ? { padding: 3, background: "#ffebee", boxShadow: "inset 0 0 0 2px #ef9a9a" } : { padding: 3 }}>{renderCell(it, c)}</td>;
                    })}
                    <td style={{ fontSize: 10, color: "#607d8b" }} title={it.updated_by ? `da ${it.updated_by}` : ""}>
                      {it.updated_at ? <>✎ {fmtDT(it.updated_at)}<br /><span style={{ fontSize: 9 }}>{it.updated_by || ""}</span></> : (it.created_at ? <>➕ {fmtDT(it.created_at)}</> : "—")}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button data-testid={`tc-mail-send-${it.Cod}`} onClick={() => apriInvio(it)} title="Invia mail a questo alunno"
                        style={{ border: "none", background: "none", cursor: "pointer", fontSize: 15 }}>📧</button>
                    </td>
                  </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {mailMgrOpen && (
        <MailModelliModal modelloId={modelloId} modelloNome={modello.nome}
          placeholders={mailPlaceholders}
          onClose={() => { setMailMgrOpen(false); loadMailTemplates(); }} />
      )}
      {bulkOpen && (
        <InvioMassivoModal modelloId={modelloId} anno={modelAnno} templates={mailTemplates}
          alunni={filtered} classiSel={filterCla}
          onClose={() => setBulkOpen(false)} />
      )}
      {storicoOpen && (
        <StoricoMailModal modelloId={modelloId} modelloNome={modello.nome}
          onClose={() => setStoricoOpen(false)} />
      )}
      {pickFor && (
        <MailPickerModal templates={mailTemplates} alunno={pickFor}
          onPick={(t) => { setSendTarget({ it: pickFor, template: t }); setPickFor(null); }}
          onClose={() => setPickFor(null)} />
      )}
      {sendTarget && (
        <InviaMailModal modelloId={modelloId} anno={modelAnno}
          template={sendTarget.template} alunno={sendTarget.it}
          onClose={() => setSendTarget(null)} />
      )}
    </div>
  );
}


function MultiListaCell({ col, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const sel = Array.isArray(value) ? value : [];
  const opts = col.opzioni || [];
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const toggle = (o) => onChange(sel.includes(o) ? sel.filter(x => x !== o) : [...sel, o]);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" data-testid={testid} onClick={() => setOpen(o => !o)}
        style={{ width: "100%", padding: "4px 6px", fontSize: 11, border: "1px solid #cfd8dc", borderRadius: 3, background: "white", textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 4, boxSizing: "border-box" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: sel.length ? "#0d47a1" : "#999", fontWeight: sel.length ? 600 : 400 }}>
          {sel.length ? sel.join(", ") : "— scegli —"}
        </span>
        <span style={{ fontSize: 9, color: "#888" }}>▼</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 2px)", left: 0, zIndex: 4000, background: "white", border: "1px solid #cfd8dc", borderRadius: 6, boxShadow: "0 6px 20px rgba(0,0,0,0.18)", minWidth: 160, maxHeight: 240, overflow: "auto", padding: 5 }}>
          {opts.length === 0 ? <div style={{ fontSize: 11, color: "#999", padding: 5 }}>Nessuna opzione</div>
            : opts.map(o => (
              <label key={o} data-testid={`${testid}-opt-${o}`} style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 5px", fontSize: 12, cursor: "pointer", borderRadius: 4, background: sel.includes(o) ? "#e3f2fd" : "transparent" }}>
                <input type="checkbox" checked={sel.includes(o)} onChange={() => toggle(o)} /> {o}
              </label>
            ))}
        </div>
      )}
    </div>
  );
}


function UtentiMultiSelect({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const toggle = (u) => onChange(selected.includes(u) ? selected.filter(x => x !== u) : [...selected, u]);
  const label = selected.length === 0 ? "Scegli utenti…" : `${selected.length} utenti`;
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button type="button" data-testid="tc-utenti-select" className="sw-filter-select" onClick={() => setOpen(o => !o)}
        style={{ minWidth: 150, textAlign: "left", cursor: "pointer", background: "white", display: "inline-flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <span style={{ fontWeight: selected.length ? 700 : 400, color: selected.length ? "#0d47a1" : "#333", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ fontSize: 10, color: "#888" }}>▼</span>
      </button>
      {open && (
        <div data-testid="tc-utenti-panel" style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 4000, background: "white", border: "1px solid #cfd8dc", borderRadius: 6, boxShadow: "0 6px 20px rgba(0,0,0,0.18)", minWidth: 230, maxHeight: 300, overflow: "auto", padding: 6 }}>
          {options.length === 0 ? <div style={{ fontSize: 12, color: "#999", padding: 6 }}>Nessun utente</div>
            : options.map(u => (
              <label key={u.username} data-testid={`tc-utenti-opt-${u.username}`} style={{ display: "flex", alignItems: "center", gap: 7, padding: "4px 6px", fontSize: 13, cursor: "pointer", borderRadius: 4, background: selected.includes(u.username) ? "#e3f2fd" : "transparent" }}>
                <input type="checkbox" checked={selected.includes(u.username)} onChange={() => toggle(u.username)} />
                {u.name} <span style={{ color: "#90a4ae", fontSize: 11 }}>({u.username})</span>
              </label>
            ))}
        </div>
      )}
    </div>
  );
}


function ClassiMultiSelect({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const toggle = (c) => onChange(selected.includes(c) ? selected.filter(x => x !== c) : [...selected, c]);
  const label = selected.length === 0 ? "Tutte" : (selected.length === 1 ? selected[0] : `${selected.length} classi`);
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button type="button" data-testid="tc-filter-classe" className="sw-filter-select" onClick={() => setOpen(o => !o)}
        style={{ minWidth: 110, textAlign: "left", cursor: "pointer", background: "white", display: "inline-flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <span style={{ fontWeight: selected.length ? 700 : 400, color: selected.length ? "#0d47a1" : "#333", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ fontSize: 10, color: "#888" }}>▼</span>
      </button>
      {open && (
        <div data-testid="tc-filter-classe-panel" style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 4000, background: "white", border: "1px solid #cfd8dc", borderRadius: 6, boxShadow: "0 6px 20px rgba(0,0,0,0.18)", minWidth: 180, maxHeight: 280, overflow: "auto", padding: 6 }}>
          <div style={{ display: "flex", gap: 6, padding: "2px 4px 6px", borderBottom: "1px solid #eee", marginBottom: 4 }}>
            <button type="button" onClick={() => onChange([...options])} style={{ flex: 1, fontSize: 11, border: "1px solid #b0bec5", borderRadius: 4, background: "#eceff1", cursor: "pointer", padding: "3px 0" }}>Tutte</button>
            <button type="button" onClick={() => onChange([])} style={{ flex: 1, fontSize: 11, border: "1px solid #b0bec5", borderRadius: 4, background: "#eceff1", cursor: "pointer", padding: "3px 0" }}>Nessuna</button>
          </div>
          {options.length === 0 ? <div style={{ fontSize: 12, color: "#999", padding: 6 }}>Nessuna classe</div>
            : options.map(c => (
              <label key={c} data-testid={`tc-filter-classe-opt-${c}`} style={{ display: "flex", alignItems: "center", gap: 7, padding: "4px 6px", fontSize: 13, cursor: "pointer", borderRadius: 4, background: selected.includes(c) ? "#e3f2fd" : "transparent" }}>
                <input type="checkbox" checked={selected.includes(c)} onChange={() => toggle(c)} />
                {c}
              </label>
            ))}
        </div>
      )}
    </div>
  );
}

/* ==================== MODELLI MAIL ==================== */
const modalOverlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 6000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflow: "auto" };
const modalCard = { background: "white", borderRadius: 8, width: "100%", maxWidth: 720, boxShadow: "0 10px 40px rgba(0,0,0,0.3)" };
const modalHead = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "2px solid #6a1b9a", background: "#f3e5f5", borderRadius: "8px 8px 0 0" };
const btnClose = { border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "#555" };
const inpS = { padding: "6px 8px", fontSize: 13, border: "1px solid #cfd8dc", borderRadius: 4, boxSizing: "border-box" };
const lblS = { fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 3 };
const DEST_LABEL = { alunno: "📧 Solo alunno", genitori: "👪 Solo genitori", entrambi: "📧👪 Alunno + genitori" };

function MailModelliModal({ modelloId, modelloNome, placeholders, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/tabelle-config/modelli/${modelloId}/mail`); setItems(r.data.items || []); }
    catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [modelloId]);

  const nuovo = () => setEditing({ nome: "", oggetto: "", corpo: "", destinatari: "entrambi", includi_file_colonne: false, reply_to: "", allegati: [] });
  const elimina = async (m) => {
    if (!window.confirm(`Eliminare il modello mail "${m.nome}"?`)) return;
    await api.delete(`/tabelle-config/mail/${m._id}`); load();
  };

  if (editing) return <MailEditor modelloId={modelloId} placeholders={placeholders} template={editing}
    onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;

  return (
    <div style={modalOverlay} data-testid="tc-mail-mgr-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalCard}>
        <div style={modalHead}>
          <div style={{ fontWeight: 800, color: "#6a1b9a" }}>✉️ Modelli mail — <span style={{ color: "#0d47a1" }}>{modelloNome}</span></div>
          <button style={btnClose} onClick={onClose} data-testid="tc-mail-mgr-close">✕</button>
        </div>
        <div style={{ padding: 16 }}>
          <button className="sw-filter-btn" data-testid="tc-mail-nuovo" onClick={nuovo}
            style={{ background: "#6a1b9a", color: "white", fontWeight: 700, marginBottom: 12 }}>+ Nuovo modello mail</button>
          {loading ? <div style={{ color: "#888", padding: 12 }}>Caricamento…</div>
            : items.length === 0 ? <div style={{ color: "#999", padding: 12, fontSize: 13 }}>Nessun modello mail. Creane uno con "+ Nuovo modello mail".</div>
              : (
                <table className="sw-table" style={{ width: "100%" }}>
                  <thead className="thead-blue"><tr><th>Nome</th><th>Oggetto</th><th style={{ width: 110, textAlign: "center" }}>Destinatari</th><th style={{ width: 160 }}>Rispondi a</th><th style={{ width: 70, textAlign: "center" }}>Allegati</th><th style={{ width: 130, textAlign: "center" }}>Azioni</th></tr></thead>
                  <tbody>
                    {items.map(m => (
                      <tr key={m._id} data-testid={`tc-mail-row-${m._id}`}>
                        <td style={{ fontWeight: 700 }}>{m.nome}</td>
                        <td style={{ fontSize: 12, color: "#555" }}>{m.oggetto || <i style={{ color: "#bbb" }}>(vuoto)</i>}</td>
                        <td style={{ textAlign: "center", fontSize: 11 }}>{DEST_LABEL[m.destinatari] || m.destinatari}</td>
                        <td style={{ fontSize: 12, color: m.reply_to ? "#0d47a1" : "#bbb" }} data-testid={`tc-mail-replyto-${m._id}`}>{m.reply_to ? `↩ ${m.reply_to}` : <i>(default)</i>}</td>
                        <td style={{ textAlign: "center" }}>{(m.allegati || []).length}{m.includi_file_colonne ? " +📎col" : ""}</td>
                        <td style={{ textAlign: "center" }}>
                          <button className="sw-filter-btn" data-testid={`tc-mail-edit-${m._id}`} onClick={() => setEditing(m)} style={{ marginRight: 6 }}>Modifica</button>
                          <button className="sw-filter-btn" data-testid={`tc-mail-del-${m._id}`} onClick={() => elimina(m)} style={{ background: "#c62828", color: "white" }}>🗑</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
        </div>
      </div>
    </div>
  );
}

function MailEditor({ modelloId, placeholders, template, onCancel, onSaved }) {
  const { user } = useAuth();
  const [m, setM] = useState({ ...template });
  const [saving, setSaving] = useState(false);
  const [activeField, setActiveField] = useState("corpo");
  const [testEmail, setTestEmail] = useState((user?.username || "").includes("@") ? user.username : "");
  const [testing, setTesting] = useState(false);
  const oggRef = useRef(null);
  const corpoRef = useRef(null);
  const fileRef = useRef(null);
  const upd = (patch) => setM(s => ({ ...s, ...patch }));

  const inviaProva = async () => {
    if (!m._id) { alert("Salva prima il modello."); return; }
    const to = (testEmail || "").trim();
    if (!to.includes("@")) { alert("Inserisci un indirizzo email valido per la prova."); return; }
    setTesting(true);
    try {
      const r = await api.post(`/tabelle-config/mail/${m._id}/send-test`, { to });
      alert(`✅ Email di prova inviata a ${r.data.to}\nAlunno di esempio: ${r.data.alunno_esempio}` + ((r.data.allegati || []).length ? `\n📎 Allegati: ${r.data.allegati.join(", ")}` : ""));
    } catch (e) { alert("Errore invio prova: " + (e.response?.data?.detail || e.message)); }
    finally { setTesting(false); }
  };

  const insertPlaceholder = (tok) => {
    const token = `{{${tok}}}`;
    const ref = activeField === "oggetto" ? oggRef : corpoRef;
    const key = activeField;
    const el = ref.current;
    if (!el) { upd({ [key]: (m[key] || "") + token }); return; }
    const start = el.selectionStart ?? (m[key] || "").length;
    const end = el.selectionEnd ?? start;
    const val = m[key] || "";
    const next = val.slice(0, start) + token + val.slice(end);
    upd({ [key]: next });
    setTimeout(() => { el.focus(); const p = start + token.length; el.setSelectionRange(p, p); }, 0);
  };

  const salva = async () => {
    if (!m.nome.trim()) { alert("Il nome del modello mail è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = { nome: m.nome, oggetto: m.oggetto, corpo: m.corpo, destinatari: m.destinatari, includi_file_colonne: m.includi_file_colonne, reply_to: m.reply_to || "" };
      let saved;
      if (m._id) { saved = (await api.put(`/tabelle-config/mail/${m._id}`, payload)).data; }
      else { saved = (await api.post(`/tabelle-config/modelli/${modelloId}/mail`, payload)).data; }
      setM(saved);
      if (!m._id) return; // resta in editor per aggiungere allegati
      onSaved();
    } catch (e) { alert("Errore salvataggio: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f || !m._id) return;
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post(`/tabelle-config/mail/${m._id}/allegati`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setM(s => ({ ...s, allegati: r.data.allegati })); }
    catch (err) { alert("Errore upload: " + (err.response?.data?.detail || err.message)); }
  };
  const delAllegato = async (fid) => {
    const r = await api.delete(`/tabelle-config/mail/${m._id}/allegati/${fid}`); setM(s => ({ ...s, allegati: r.data.allegati }));
  };

  return (
    <div style={modalOverlay} data-testid="tc-mail-editor" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={modalCard}>
        <div style={modalHead}>
          <div style={{ fontWeight: 800, color: "#6a1b9a" }}>{m._id ? "✏️ Modifica modello mail" : "➕ Nuovo modello mail"}</div>
          <button style={btnClose} onClick={onCancel}>✕</button>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1.4fr", gap: 12 }}>
            <div><label style={lblS}>Nome modello *</label>
              <input data-testid="tc-mail-nome" style={{ ...inpS, width: "100%" }} value={m.nome} onChange={e => upd({ nome: e.target.value })} placeholder="es. Richiesta certificato" /></div>
            <div><label style={lblS}>Destinatari</label>
              <select data-testid="tc-mail-destinatari" style={{ ...inpS, width: "100%" }} value={m.destinatari} onChange={e => upd({ destinatari: e.target.value })}>
                <option value="entrambi">Alunno + genitori</option>
                <option value="alunno">Solo email alunno</option>
                <option value="genitori">Solo email genitori</option>
              </select></div>
          </div>
          <div><label style={lblS}>↩ Rispondi a (Reply-To)</label>
            <input data-testid="tc-mail-replyto" type="email" style={{ ...inpS, width: "100%" }} value={m.reply_to || ""}
              onChange={e => upd({ reply_to: e.target.value })} placeholder="es. segreteria@canossadiaz.it — dove arrivano le risposte (vuoto = default di sistema)" />
            <div style={{ fontSize: 11, color: "#607d8b", marginTop: 3 }}>Quando il destinatario risponde alla mail, la risposta arriva a questo indirizzo. Se vuoto si usa l'indirizzo predefinito del sistema.</div>
          </div>
          <div><label style={lblS}>Oggetto</label>
            <input ref={oggRef} data-testid="tc-mail-oggetto" style={{ ...inpS, width: "100%" }} value={m.oggetto}
              onFocus={() => setActiveField("oggetto")} onChange={e => upd({ oggetto: e.target.value })} placeholder="Oggetto (puoi usare i placeholder)" /></div>
          <div><label style={lblS}>Corpo del messaggio</label>
            <textarea ref={corpoRef} data-testid="tc-mail-corpo" rows={8} style={{ ...inpS, width: "100%", fontFamily: "inherit" }} value={m.corpo}
              onFocus={() => setActiveField("corpo")} onChange={e => upd({ corpo: e.target.value })} placeholder="Testo email. Clicca un placeholder qui sotto per inserirlo dove hai il cursore." /></div>

          <div style={{ background: "#f3e5f5", borderRadius: 6, padding: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6a1b9a", marginBottom: 6 }}>Placeholder disponibili (clic per inserire in "{activeField === "oggetto" ? "Oggetto" : "Corpo"}"):</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {(placeholders || []).map(p => (
                <button key={p.token} data-testid={`tc-mail-ph-${p.token}`} onClick={() => insertPlaceholder(p.token)} title={`${p.gruppo} · {{${p.token}}}`}
                  style={{ fontSize: 11, background: p.gruppo === "Anagrafica" ? "#e1bee7" : "#c5cae9", border: "1px solid #b39ddb", borderRadius: 12, padding: "3px 10px", cursor: "pointer", color: "#4a148c", fontWeight: 600 }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#333" }}>
            <input type="checkbox" data-testid="tc-mail-includi-file" checked={!!m.includi_file_colonne} onChange={e => upd({ includi_file_colonne: e.target.checked })} />
            📎 Allega automaticamente i file delle colonne "file" dell'alunno (es. il suo certificato)
          </label>

          <div style={{ borderTop: "1px dashed #ccc", paddingTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#333", marginBottom: 6 }}>📎 Allegati fissi (uguali per tutti gli alunni)</div>
            {!m._id ? <div style={{ fontSize: 12, color: "#c62828" }}>💾 Salva prima il modello per poter aggiungere allegati fissi.</div>
              : (
                <>
                  {(m.allegati || []).length === 0 && <div style={{ fontSize: 12, color: "#999", marginBottom: 6 }}>Nessun allegato fisso.</div>}
                  {(m.allegati || []).map(a => (
                    <div key={a.file_id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "3px 0" }}>
                      <span>📄 {a.filename} <span style={{ color: "#999" }}>({Math.round((a.size || 0) / 1024)} KB)</span></span>
                      <button data-testid={`tc-mail-del-allegato-${a.file_id}`} onClick={() => delAllegato(a.file_id)} style={{ border: "none", background: "none", cursor: "pointer", color: "#c62828" }}>✕</button>
                    </div>
                  ))}
                  <input ref={fileRef} type="file" style={{ display: "none" }} onChange={onFile} />
                  <button className="sw-filter-btn" data-testid="tc-mail-add-allegato" onClick={() => fileRef.current?.click()} style={{ marginTop: 6 }}>⬆ Aggiungi allegato fisso</button>
                </>
              )}
          </div>

          <div style={{ borderTop: "1px dashed #ccc", paddingTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#333", marginBottom: 6 }}>📨 Invio di prova</div>
            {!m._id ? <div style={{ fontSize: 12, color: "#c62828" }}>💾 Salva prima il modello per inviare una prova.</div>
              : (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <input data-testid="tc-mail-test-email" style={{ ...inpS, width: 260 }} value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="email dove ricevere la prova" />
                  <button className="sw-filter-btn" data-testid="tc-mail-test-send" disabled={testing} onClick={inviaProva}
                    style={{ background: "#00838f", color: "white", fontWeight: 700 }}>{testing ? "Invio…" : "📨 Invia prova"}</button>
                  <span style={{ fontSize: 11, color: "#607d8b" }}>Riceverai la mail (oggetto con prefisso <b>[PROVA]</b>) compilata con un alunno di esempio e i tuoi allegati.</span>
                </div>
              )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1px solid #eee", paddingTop: 12 }}>
            <button className="sw-filter-btn" onClick={onCancel}>{m._id ? "Chiudi" : "Annulla"}</button>
            <button className="sw-filter-btn" data-testid="tc-mail-save" disabled={saving} onClick={salva}
              style={{ background: "#6a1b9a", color: "white", fontWeight: 700 }}>{saving ? "Salvo…" : (m._id ? "💾 Salva modifiche" : "💾 Salva e continua")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MailPickerModal({ templates, alunno, label, onPick, onClose }) {
  const dest = label || (alunno ? `${alunno.Cog} ${alunno.Nom}` : "");
  return (
    <div style={modalOverlay} data-testid="tc-mail-picker" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...modalCard, maxWidth: 460 }}>
        <div style={modalHead}>
          <div style={{ fontWeight: 800, color: "#6a1b9a" }}>✉️ Scegli il modello mail</div>
          <button style={btnClose} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 10 }}>Destinatario: <b>{dest}</b></div>
          <div style={{ display: "grid", gap: 8 }}>
            {templates.map(t => (
              <button key={t._id} data-testid={`tc-mail-pick-${t._id}`} onClick={() => onPick(t)}
                style={{ textAlign: "left", border: "1px solid #b39ddb", background: "#f3e5f5", borderRadius: 6, padding: "10px 12px", cursor: "pointer" }}>
                <div style={{ fontWeight: 700, color: "#4a148c" }}>{t.nome}</div>
                <div style={{ fontSize: 12, color: "#666" }}>{t.oggetto || <i>(oggetto vuoto)</i>}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function InviaMailModal({ modelloId, anno, template, alunno, onClose }) {
  const [loading, setLoading] = useState(true);
  const [oggetto, setOggetto] = useState("");
  const [corpo, setCorpo] = useState("");
  const [emails, setEmails] = useState([]);
  const [emailsText, setEmailsText] = useState("");
  const [allegatiFissi, setAllegatiFissi] = useState([]);
  const [allegatiColonne, setAllegatiColonne] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/tabelle-config/mail/${template._id}/preview/${alunno.Cod}`, { params: { anno } });
        setOggetto(r.data.oggetto || "");
        setCorpo(r.data.corpo || "");
        setEmails(r.data.emails || []);
        setEmailsText((r.data.emails || []).join(", "));
        setAllegatiFissi(r.data.allegati_fissi || []);
        setAllegatiColonne(r.data.allegati_colonne || []);
      } catch (e) { alert("Errore anteprima: " + (e.response?.data?.detail || e.message)); onClose(); }
      finally { setLoading(false); }
    })();
  }, [template._id, alunno.Cod]);

  const invia = async () => {
    const to = emailsText.split(",").map(s => s.trim()).filter(Boolean);
    if (!to.length) { alert("Inserisci almeno un destinatario email"); return; }
    setSending(true);
    try {
      const r = await api.post(`/tabelle-config/mail/${template._id}/send/${alunno.Cod}`, { to, oggetto, corpo }, { params: { anno } });
      setResult(r.data);
    } catch (e) { alert("Errore invio: " + (e.response?.data?.detail || e.message)); }
    finally { setSending(false); }
  };

  const totAllegati = allegatiFissi.length + allegatiColonne.length;

  return (
    <div style={modalOverlay} data-testid="tc-invia-mail-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalCard}>
        <div style={modalHead}>
          <div style={{ fontWeight: 800, color: "#6a1b9a" }}>📧 Invia mail — <span style={{ color: "#0d47a1" }}>{alunno.Cog} {alunno.Nom}</span></div>
          <button style={btnClose} onClick={onClose} data-testid="tc-invia-close">✕</button>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          {loading ? <div style={{ color: "#888", padding: 20 }}>Caricamento anteprima…</div>
            : result ? (
              <div data-testid="tc-invia-result" style={{ padding: 8 }}>
                {result.status === "sent" ? <div style={{ color: "#2e7d32", fontWeight: 700, fontSize: 15 }}>✅ Mail inviata a {result.sent.length} destinatario/i.</div>
                  : result.status === "sent_partial" ? <div style={{ color: "#ef6c00", fontWeight: 700 }}>⚠ Inviata solo ad alcuni destinatari.</div>
                    : <div style={{ color: "#c62828", fontWeight: 700 }}>❌ Invio fallito.</div>}
                {(result.sent || []).length > 0 && <div style={{ fontSize: 12, marginTop: 8, color: "#333" }}>Inviata a: {result.sent.map(s => s.email).join(", ")}</div>}
                {(result.errors || []).length > 0 && <div style={{ fontSize: 12, marginTop: 6, color: "#c62828" }}>{result.errors.map((e, i) => <div key={i}>{e.email}: {e.error}</div>)}</div>}
                {(result.allegati || []).length > 0 && <div style={{ fontSize: 12, marginTop: 6, color: "#555" }}>📎 Allegati: {result.allegati.join(", ")}</div>}
                <div style={{ textAlign: "right", marginTop: 14 }}><button className="sw-filter-btn" onClick={onClose} style={{ background: "#6a1b9a", color: "white", fontWeight: 700 }}>Chiudi</button></div>
              </div>
            ) : (
              <>
                <div><label style={lblS}>Destinatari (separati da virgola)</label>
                  <input data-testid="tc-invia-to" style={{ ...inpS, width: "100%" }} value={emailsText} onChange={e => setEmailsText(e.target.value)} placeholder="email1@..., email2@..." />
                  {emails.length === 0 && <div style={{ fontSize: 11, color: "#c62828", marginTop: 3 }}>⚠ Nessuna email trovata per questo alunno: inseriscila manualmente.</div>}</div>
                <div><label style={lblS}>Oggetto</label>
                  <input data-testid="tc-invia-oggetto" style={{ ...inpS, width: "100%" }} value={oggetto} onChange={e => setOggetto(e.target.value)} /></div>
                <div><label style={lblS}>Messaggio</label>
                  <textarea data-testid="tc-invia-corpo" rows={9} style={{ ...inpS, width: "100%", fontFamily: "inherit" }} value={corpo} onChange={e => setCorpo(e.target.value)} /></div>
                {totAllegati > 0 && (
                  <div style={{ background: "#f5f5f5", borderRadius: 6, padding: 8, fontSize: 12, color: "#444" }}>
                    📎 <b>{totAllegati}</b> allegato/i: {[...allegatiFissi, ...allegatiColonne].join(", ")}
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1px solid #eee", paddingTop: 12 }}>
                  <button className="sw-filter-btn" onClick={onClose}>Annulla</button>
                  <button className="sw-filter-btn" data-testid="tc-invia-send" disabled={sending} onClick={invia}
                    style={{ background: "#6a1b9a", color: "white", fontWeight: 700 }}>{sending ? "Invio…" : "📧 Invia ora"}</button>
                </div>
              </>
            )}
        </div>
      </div>
    </div>
  );
}


function InviaMailRowModal({ modelloId, anno, template, rowId, email, onClose }) {
  const [loading, setLoading] = useState(true);
  const [oggetto, setOggetto] = useState("");
  const [corpo, setCorpo] = useState("");
  const [emailsText, setEmailsText] = useState(email || "");
  const [allegatiFissi, setAllegatiFissi] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/tabelle-config/mail/${template._id}/preview-row/${rowId}`, { params: { anno } });
        setOggetto(r.data.oggetto || "");
        setCorpo(r.data.corpo || "");
        const em = (r.data.emails || []);
        setEmailsText(em.length ? em.join(", ") : (email || ""));
        setAllegatiFissi(r.data.allegati_fissi || []);
      } catch (e) { alert("Errore anteprima: " + (e.response?.data?.detail || e.message)); onClose(); }
      finally { setLoading(false); }
    })();
  }, [template._id, rowId]);

  const invia = async () => {
    const to = emailsText.split(",").map(s => s.trim()).filter(Boolean);
    if (!to.length) { alert("Inserisci almeno un destinatario email"); return; }
    setSending(true);
    try {
      const r = await api.post(`/tabelle-config/mail/${template._id}/send-row/${rowId}`, { to, oggetto, corpo }, { params: { anno } });
      setResult(r.data);
    } catch (e) { alert("Errore invio: " + (e.response?.data?.detail || e.message)); }
    finally { setSending(false); }
  };

  return (
    <div style={modalOverlay} data-testid="tc-invia-mail-row-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalCard}>
        <div style={modalHead}>
          <div style={{ fontWeight: 800, color: "#6a1b9a" }}>📧 Invia mail — <span style={{ color: "#0d47a1" }}>{email}</span></div>
          <button style={btnClose} onClick={onClose} data-testid="tc-invia-row-close">✕</button>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          {loading ? <div style={{ color: "#888", padding: 20 }}>Caricamento anteprima…</div>
            : result ? (
              <div data-testid="tc-invia-row-result" style={{ padding: 8 }}>
                {result.status === "sent" ? <div style={{ color: "#2e7d32", fontWeight: 700, fontSize: 15 }}>✅ Mail inviata a {result.sent.length} destinatario/i.</div>
                  : result.status === "sent_partial" ? <div style={{ color: "#ef6c00", fontWeight: 700 }}>⚠ Inviata solo ad alcuni destinatari.</div>
                    : <div style={{ color: "#c62828", fontWeight: 700 }}>❌ Invio fallito.</div>}
                {(result.sent || []).length > 0 && <div style={{ fontSize: 12, marginTop: 8, color: "#333" }}>Inviata a: {result.sent.map(s => s.email).join(", ")}</div>}
                {(result.errors || []).length > 0 && <div style={{ fontSize: 12, marginTop: 6, color: "#c62828" }}>{result.errors.map((e, i) => <div key={i}>{e.email}: {e.error}</div>)}</div>}
                <div style={{ textAlign: "right", marginTop: 14 }}><button className="sw-filter-btn" onClick={onClose} style={{ background: "#6a1b9a", color: "white", fontWeight: 700 }}>Chiudi</button></div>
              </div>
            ) : (
              <>
                <div><label style={lblS}>Destinatari (separati da virgola)</label>
                  <input data-testid="tc-invia-row-to" style={{ ...inpS, width: "100%" }} value={emailsText} onChange={e => setEmailsText(e.target.value)} placeholder="email1@..., email2@..." />
                  {!emailsText.trim() && <div style={{ fontSize: 11, color: "#c62828", marginTop: 3 }}>⚠ Nessuna email nel campo 'email' di questa riga: inseriscila manualmente.</div>}</div>
                <div><label style={lblS}>Oggetto</label>
                  <input data-testid="tc-invia-row-oggetto" style={{ ...inpS, width: "100%" }} value={oggetto} onChange={e => setOggetto(e.target.value)} /></div>
                <div><label style={lblS}>Messaggio</label>
                  <textarea data-testid="tc-invia-row-corpo" rows={9} style={{ ...inpS, width: "100%", fontFamily: "inherit" }} value={corpo} onChange={e => setCorpo(e.target.value)} /></div>
                {allegatiFissi.length > 0 && (
                  <div style={{ background: "#f5f5f5", borderRadius: 6, padding: 8, fontSize: 12, color: "#444" }}>
                    📎 <b>{allegatiFissi.length}</b> allegato/i: {allegatiFissi.join(", ")}
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1px solid #eee", paddingTop: 12 }}>
                  <button className="sw-filter-btn" onClick={onClose}>Annulla</button>
                  <button className="sw-filter-btn" data-testid="tc-invia-row-send" disabled={sending} onClick={invia}
                    style={{ background: "#6a1b9a", color: "white", fontWeight: 700 }}>{sending ? "Invio…" : "📧 Invia ora"}</button>
                </div>
              </>
            )}
        </div>
      </div>
    </div>
  );
}


function InvioMassivoModal({ modelloId, anno, templates, alunni, classiSel, onClose }) {
  const [tid, setTid] = useState(templates.length === 1 ? templates[0]._id : "");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [showSenza, setShowSenza] = useState(false);
  const template = templates.find(t => t._id === tid);
  const cods = (alunni || []).map(a => a.Cod);
  const DEST = { alunno: "solo alunno", genitori: "solo genitori", entrambi: "alunno + genitori" };

  useEffect(() => {
    setPreview(null); setShowSenza(false);
    if (!tid || !cods.length) return;
    let ok = true;
    setLoadingPrev(true);
    api.post(`/tabelle-config/mail/${tid}/send-bulk/preview`, { cods }, { params: { anno } })
      .then(r => { if (ok) setPreview(r.data); })
      .catch(() => { if (ok) setPreview(null); })
      .finally(() => { if (ok) setLoadingPrev(false); });
    return () => { ok = false; };
  }, [tid]);

  const invia = async () => {
    if (!tid) { alert("Scegli un modello mail"); return; }
    if (!cods.length) { alert("Nessun alunno da inviare"); return; }
    const conf = preview ? `Confermi l'invio a ${preview.con_email} alunni (${preview.email_totali} email)? ${preview.senza_email.length} senza email verranno esclusi.` : `Confermi l'invio della mail "${template?.nome}" a ${cods.length} alunni?`;
    if (!window.confirm(conf)) return;
    setSending(true);
    try {
      const r = await api.post(`/tabelle-config/mail/${tid}/send-bulk`, { cods }, { params: { anno } });
      setResult(r.data);
    } catch (e) { alert("Errore invio massivo: " + (e.response?.data?.detail || e.message)); }
    finally { setSending(false); }
  };

  return (
    <div style={modalOverlay} data-testid="tc-bulk-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...modalCard, maxWidth: 560 }}>
        <div style={{ ...modalHead, borderBottomColor: "#ad1457", background: "#fce4ec" }}>
          <div style={{ fontWeight: 800, color: "#ad1457" }}>📤 Invio massivo</div>
          <button style={btnClose} onClick={onClose} data-testid="tc-bulk-close">✕</button>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          {result ? (
            <div data-testid="tc-bulk-result">
              <div style={{ fontSize: 15, fontWeight: 700, color: result.inviati > 0 ? "#2e7d32" : "#c62828" }}>
                {result.inviati > 0 ? `✅ Inviata a ${result.inviati} alunni (${result.email_totali} email totali).` : "❌ Nessuna mail inviata."}
              </div>
              <div style={{ fontSize: 12, color: "#555", marginTop: 8 }}>Alunni elaborati: {result.totale_alunni}</div>
              {(result.senza_email || []).length > 0 && (
                <div style={{ fontSize: 12, color: "#ef6c00", marginTop: 8 }}>
                  ⚠ {result.senza_email.length} senza email: {result.senza_email.map(s => s.nome).join(", ")}
                </div>
              )}
              {(result.falliti || []).length > 0 && (
                <div style={{ fontSize: 12, color: "#c62828", marginTop: 8 }}>
                  ❌ {result.falliti.length} falliti: {result.falliti.map(s => `${s.nome || s.cod}`).join(", ")}
                </div>
              )}
              {(result.allegati || []).length > 0 && <div style={{ fontSize: 12, color: "#555", marginTop: 6 }}>📎 Allegati fissi: {result.allegati.join(", ")}</div>}
              <div style={{ textAlign: "right", marginTop: 14 }}><button className="sw-filter-btn" onClick={onClose} style={{ background: "#ad1457", color: "white", fontWeight: 700 }}>Chiudi</button></div>
            </div>
          ) : (
            <>
              <div style={{ background: "#fff3e0", border: "1px solid #ffcc80", borderRadius: 6, padding: 10, fontSize: 13, color: "#5d4037" }}>
                Verrà inviata la mail a <b>{cods.length}</b> alunni attualmente filtrati
                {classiSel && classiSel.length ? <> (classi: <b>{classiSel.join(", ")}</b>)</> : <> (tutte le classi)</>}.
              </div>
              <div><label style={lblS}>Modello mail</label>
                <select data-testid="tc-bulk-template" style={{ ...inpS, width: "100%" }} value={tid} onChange={e => setTid(e.target.value)}>
                  <option value="">— scegli —</option>
                  {templates.map(t => <option key={t._id} value={t._id}>{t.nome}</option>)}
                </select></div>
              {template && (
                <div style={{ background: "#f5f5f5", borderRadius: 6, padding: 10, fontSize: 12, color: "#444" }}>
                  <div><b>Oggetto:</b> {template.oggetto || <i>(vuoto)</i>}</div>
                  <div style={{ marginTop: 4 }}><b>Destinatari:</b> {DEST[template.destinatari] || template.destinatari}</div>
                  <div style={{ marginTop: 4 }}><b>Allegati fissi:</b> {(template.allegati || []).length}{template.includi_file_colonne ? " + file colonne alunno" : ""}</div>
                  <div style={{ marginTop: 6, fontSize: 11, color: "#777" }}>I placeholder verranno personalizzati per ciascun alunno.</div>
                </div>
              )}
              {template && (loadingPrev ? (
                <div style={{ fontSize: 12, color: "#888" }}>Calcolo destinatari…</div>
              ) : preview && (
                <div data-testid="tc-bulk-preview" style={{ background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 6, padding: 10, fontSize: 12, color: "#2e4630" }}>
                  <div>📬 <b>{preview.con_email}</b> alunni riceveranno la mail (<b>{preview.email_totali}</b> email totali).</div>
                  {preview.senza_email.length > 0 ? (
                    <div style={{ marginTop: 6, color: "#c62828" }}>
                      ⚠ <b>{preview.senza_email.length}</b> alunni <b>senza email</b> saranno esclusi.{" "}
                      <button data-testid="tc-bulk-toggle-senza" onClick={() => setShowSenza(s => !s)} style={{ border: "none", background: "none", color: "#0d47a1", cursor: "pointer", textDecoration: "underline", fontSize: 12, padding: 0 }}>{showSenza ? "nascondi elenco" : "mostra elenco"}</button>
                      {showSenza && (
                        <ul style={{ margin: "6px 0 0", paddingLeft: 18, maxHeight: 140, overflow: "auto" }}>
                          {preview.senza_email.map(s => <li key={s.cod}>{s.nome} <span style={{ color: "#999" }}>({s.cod})</span></li>)}
                        </ul>
                      )}
                    </div>
                  ) : <div style={{ marginTop: 6, color: "#2e7d32" }}>✓ Tutti gli alunni filtrati hanno almeno un'email.</div>}
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1px solid #eee", paddingTop: 12 }}>
                <button className="sw-filter-btn" onClick={onClose}>Annulla</button>
                <button className="sw-filter-btn" data-testid="tc-bulk-send" disabled={sending || !tid || !cods.length || loadingPrev || (preview && preview.con_email === 0)} onClick={invia}
                  style={{ background: "#ad1457", color: "white", fontWeight: 700 }}>{sending ? "Invio in corso… (attendere)" : `📤 Invia a ${preview ? preview.con_email : cods.length} alunni`}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function StoricoMailModal({ modelloId, modelloNome, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  const ESITO = {
    sent: { label: "✅ Inviata", color: "#2e7d32" },
    sent_partial: { label: "⚠ Parziale", color: "#ef6c00" },
    error: { label: "❌ Errore", color: "#c62828" },
  };
  useEffect(() => {
    setLoading(true);
    api.get(`/tabelle-config/modelli/${modelloId}/storico-mail`)
      .then(r => setItems(r.data.items || []))
      .catch(e => alert("Errore: " + (e.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [modelloId]);

  return (
    <div style={modalOverlay} data-testid="tc-storico-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...modalCard, maxWidth: 900 }}>
        <div style={{ ...modalHead, borderBottomColor: "#455a64", background: "#eceff1" }}>
          <div style={{ fontWeight: 800, color: "#37474f" }}>🕓 Storico mail inviate — <span style={{ color: "#0d47a1" }}>{modelloNome}</span></div>
          <button style={btnClose} onClick={onClose} data-testid="tc-storico-close">✕</button>
        </div>
        <div style={{ padding: 16, maxHeight: "72vh", overflow: "auto" }}>
          {loading ? <div style={{ color: "#888", padding: 12 }}>Caricamento…</div>
            : items.length === 0 ? <div style={{ color: "#999", padding: 12, fontSize: 13 }}>Nessuna mail inviata da questa tabella.</div>
              : (
                <table className="sw-table" style={{ width: "100%" }}>
                  <thead className="thead-blue"><tr>
                    <th style={{ width: 120 }}>Data</th>
                    <th style={{ minWidth: 150 }}>Alunno</th>
                    <th>Oggetto</th>
                    <th style={{ minWidth: 160 }}>Destinatari</th>
                    <th style={{ width: 90, textAlign: "center" }}>Esito</th>
                    <th style={{ width: 70, textAlign: "center" }}>Tipo</th>
                  </tr></thead>
                  <tbody>
                    {items.map(it => {
                      const es = ESITO[it.status] || { label: it.status || "—", color: "#555" };
                      return (
                        <tr key={it.id} data-testid={`tc-storico-row-${it.id}`}>
                          <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>{fmt(it.created_at)}<br /><span style={{ fontSize: 9, color: "#90a4ae" }}>{it.created_by || ""}</span></td>
                          <td style={{ fontSize: 12, fontWeight: 600 }}>{it.alunno}</td>
                          <td style={{ fontSize: 12 }}>{it.oggetto || <i style={{ color: "#bbb" }}>(vuoto)</i>}
                            {(it.allegati || []).length > 0 && <div style={{ fontSize: 10, color: "#607d8b" }}>📎 {it.allegati.join(", ")}</div>}
                            {it.errore && <div style={{ fontSize: 10, color: "#c62828" }}>{it.errore}</div>}
                          </td>
                          <td style={{ fontSize: 11 }}>{(it.destinatari || []).join(", ")}</td>
                          <td style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: es.color }}>{es.label}</td>
                          <td style={{ textAlign: "center", fontSize: 10 }}>{it.massivo ? "📤 massivo" : "📧 singolo"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
        </div>
      </div>
    </div>
  );
}

